/* eslint-disable */
/* global Office, Excel */

const BACKEND_URL = "https://web-production-57a75a.up.railway.app";

// Confidence factors pour MUS (standard audit)
const MUS_CF = {
  low_risk: 1.0,
  moderate_risk: 1.5,
  high_risk: 3.0,
};

function calculateMusSampleSize(populationValue: number, riskLevel: string, tolerableMisstatement: number): number {
  if (tolerableMisstatement <= 0) return 0;
  const cf = MUS_CF[riskLevel as keyof typeof MUS_CF] ?? 1.0;
  const absPopulation = Math.abs(populationValue);
  const n = Math.ceil((absPopulation * cf) / tolerableMisstatement);
  return Math.max(0, n);
}

// ============================================================
// SHARED STATE
// ============================================================
type SheetMapping = {
  // GL fields
  id?: string;
  compte?: string;
  date?: string;
  journal?: string;
  libelle?: string;
  amount_mode?: "single" | "split";
  montant?: string;
  debit?: string;
  credit?: string;
  // TB fields
  solde?: string;
  // Chart fields
  financial_line_statutory?: string;
  financial_line_abcotd?: string;
  statement_type?: string;
};

const state: {
  materiality: number;
  pmPct: number;
  cttPct: number;
  sheets: { gl: string | null; tb: string | null; chart: string | null; tb_n1: string | null };
  mappings: { gl: SheetMapping; tb: SheetMapping; tb_n1: SheetMapping; chart: SheetMapping };
  mappingsValidated: { gl: boolean; tb: boolean; tb_n1: boolean; chart: boolean };
  validated: boolean;
} = {
  materiality: 100000,
  pmPct: 80,
  cttPct: 5,
  sheets: { gl: null, tb: null, chart: null, tb_n1: null },
  mappings: { gl: {}, tb: {}, tb_n1: {}, chart: {} },
  mappingsValidated: { gl: false, tb: false, tb_n1: false, chart: false },
  validated: false,
};


// ============================================================
// JOURNAL MULTI-SELECT (réutilisable)
// ============================================================
const journalSelect = {
  cache: null as string[] | null,  // valeurs uniques détectées
  selections: new Map<string, Set<string>>(),  // par instance ID
};

async function detectJournals(): Promise<string[]> {
  if (journalSelect.cache) return journalSelect.cache;
  if (!state.sheets.gl) return [];

  try {
    const glRows = await readMappedSheet("gl");
    const journals = new Set<string>();
    for (const row of glRows) {
      const j = row["journal"];
      if (j !== null && j !== undefined && String(j).trim() !== "") {
        journals.add(String(j).trim());
      }
    }
    journalSelect.cache = Array.from(journals).sort();
    return journalSelect.cache;
  } catch (err) {
    console.error("Journal detection failed:", err);
    return [];
  }
}

function invalidateJournalCache() {
  journalSelect.cache = null;
}

async function loadReversalsSections() {
  const container = document.getElementById("rev-sections-container")!;
  const summary = document.getElementById("rev-sections-summary")!;

  if (!state.sheets.gl || !state.sheets.chart) {
    container.innerHTML = `<p class="hint" style="padding:8px">Setup et Scoping requis.</p>`;
    summary.style.display = "none";
    return;
  }

  try {
    // Lire les sections depuis scopingState (en mémoire)
    if (!scopingState.loaded || scopingState.rows.length === 0) {
      container.innerHTML = `<p class="hint" style="padding:8px">Lance d'abord l'étape 3 (Scoping).</p>`;
      summary.style.display = "none";
      return;
    }
    const scopingRows = scopingState.rows;

    // Identifier la colonne nom et in_scope
    const sample = scopingRows[0];
    const lineKey = "financial_line" in sample ? "financial_line"
                  : "label" in sample ? "label"
                  : Object.keys(sample)[0];
    const inScopeKey = "in_scope" in sample ? "in_scope" : null;

    const inScopeRows = inScopeKey
      ? scopingRows.filter((r: any) => r[inScopeKey] === true || r[inScopeKey] === "TRUE")
      : scopingRows;

    if (inScopeRows.length === 0) {
      container.innerHTML = `<p class="hint" style="padding:8px">Aucune section in-scope.</p>`;
      summary.style.display = "none";
      return;
    }

    // Lire le Chart pour mapper les comptes
    const chartRows = await readMappedSheet("chart");
    // On utilise statutory par défaut (correspond à l'output Scoping standard)
    const granularityCol = "financial_line_statutory" in chartRows[0]
      ? "financial_line_statutory"
      : "financial_line_abcotd";

    // Préserver l'état checked des sections existantes (par nom)
    const previousChecked = new Map<string, boolean>();
    for (const old of reversalsState.sections) {
      previousChecked.set(old.name, old.checked);
    }

    const newSections = inScopeRows.map((row: any) => {
      const name = String(row[lineKey]);
      const accounts = chartRows
        .filter((c: any) => String(c[granularityCol]) === name)
        .map((c: any) => String(c["compte"]));
      const amount = parseFloat(row["abs_value"] ?? row["value"] ?? row["total_value"] ?? "0") || 0;
      // Si la section existait déjà, garder son état checked. Sinon par défaut true.
      const checked = previousChecked.has(name) ? previousChecked.get(name)! : true;
      return { name, accounts, amount, checked };
    });

    reversalsState.sections = newSections;
    reversalsState.loaded = true;

    renderReversalsSections();
  } catch (err: any) {
    console.error(err);
    container.innerHTML = `<p class="hint" style="padding:8px;color:var(--danger)">Erreur : ${err.message}</p>`;
    summary.style.display = "none";
  }
}

function renderReversalsSections() {
  const container = document.getElementById("rev-sections-container")!;
  const summary = document.getElementById("rev-sections-summary")!;
  const countEl = document.getElementById("rev-sections-count")!;

  if (reversalsState.sections.length === 0) {
    container.innerHTML = `<p class="hint" style="padding:8px">Aucune section disponible.</p>`;
    summary.style.display = "none";
    return;
  }

  container.innerHTML = reversalsState.sections.map((sec, idx) => `
    <label class="section-row ${sec.checked ? "" : "unchecked"}" data-idx="${idx}">
      <input type="checkbox" ${sec.checked ? "checked" : ""}>
      <span class="section-name">${formatLabel(sec.name)}</span>
      <span class="section-amount">${formatEur(sec.amount)}</span>
    </label>
  `).join("");

  container.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach(cb => {
    cb.onchange = () => {
      const row = cb.closest(".section-row") as HTMLElement;
      const idx = parseInt(row.dataset.idx!);
      reversalsState.sections[idx].checked = cb.checked;
      row.classList.toggle("unchecked", !cb.checked);
      updateReversalsSummary();
    };
  });

  const checkedCount = reversalsState.sections.filter(s => s.checked).length;
  countEl.textContent = String(checkedCount);
  summary.style.display = "block";
  updateReversalsSummary();
}

// ============================================================
// MAPPING UI
// ============================================================

function bindMappingAccordions() {
  document.querySelectorAll<HTMLElement>(".mapping-header").forEach(header => {
    header.onclick = () => {
      const sheetType = header.dataset.target as "gl" | "tb" | "tb_n1" | "chart";
      const accordion = header.closest(".mapping-accordion") as HTMLElement;
      const body = document.getElementById(`map-body-${sheetType}`)!;
      const isOpen = accordion.classList.contains("expanded");

      // Fermer tous les autres
      document.querySelectorAll<HTMLElement>(".mapping-accordion").forEach(a => {
        a.classList.remove("expanded");
        const id = (a.querySelector(".mapping-header") as HTMLElement).dataset.target!;
        document.getElementById(`map-body-${id}`)!.style.display = "none";
      });

      // Ouvrir celui-ci si fermé
      if (!isOpen) {
        accordion.classList.add("expanded");
        body.style.display = "block";
        renderMappingBody(sheetType);
      }
    };
  });
}

async function preloadMappingForSheet(sheetType: "gl" | "tb" | "tb_n1" | "chart"): Promise<void> {
  const sheetName = state.sheets[sheetType];
  if (!sheetName) return;
  try {
    const headers = await readSheetHeaders(sheetName);
    if (headers.length === 0) return;
    if (Object.keys(state.mappings[sheetType]).length === 0) {
      state.mappings[sheetType] = autoDetectMapping(headers, sheetType);
    }
  } catch (err) {
    console.warn(`Préchargement mapping ${sheetType} échoué :`, err);
  }
}

async function renderMappingBody(sheetType: "gl" | "tb" | "tb_n1" | "chart") {
  const body = document.getElementById(`map-body-${sheetType}`)!;
  const sheetName = state.sheets[sheetType];

  if (!sheetName) {
    body.innerHTML = `<p class="hint" style="color:var(--warning)">Aucune feuille sélectionnée pour ce rôle. Choisis-la dans Données.</p>`;
    return;
  }

  body.innerHTML = `<p class="hint">Lecture des colonnes de ${sheetName}...</p>`;

  try {
    const headers = await readSheetHeaders(sheetName);
    if (headers.length === 0) {
      body.innerHTML = `<p class="hint" style="color:var(--danger)">Feuille vide.</p>`;
      return;
    }

    // Auto-détecter si pas encore mappé
    if (Object.keys(state.mappings[sheetType]).length === 0) {
      state.mappings[sheetType] = autoDetectMapping(headers, sheetType);
    }

    const mapping = state.mappings[sheetType];

    // Construire la liste de champs à afficher
    const reqs = getRequiredFieldsForSheet(sheetType);

    let html = "";

    // Champs core
    for (const field of reqs.core) {
      const fieldLabel = getFieldLabel(field);
      const currentValue = (mapping as any)[field] ?? "";
      const optionsHtml = headers.map(h =>
        `<option value="${h}" ${h === currentValue ? "selected" : ""}>${h}</option>`
      ).join("");
      html += `
        <div class="mapping-field">
          <span class="mapping-field-label">${fieldLabel}</span>
          <select class="mapping-field-select ${currentValue ? "" : "unset"}" data-sheet="${sheetType}" data-field="${field}">
            <option value="">— sélectionner —</option>
            ${optionsHtml}
          </select>
        </div>
      `;
    }
    // Champ optionnel : id (line ID) pour le GL uniquement
    if (sheetType === "gl") {
      const idValue = mapping.id ?? "";
      const idOptionsHtml = headers.map(h =>
        `<option value="${h}" ${h === idValue ? "selected" : ""}>${h}</option>`
      ).join("");
      html += `
        <div class="mapping-field">
          <span class="mapping-field-label">Line ID <span style="color:var(--text-subtle); font-weight:400; font-size:11px">(optionnel)</span></span>
          <select class="mapping-field-select ${idValue ? "" : ""}" data-sheet="${sheetType}" data-field="id">
            <option value="">— Auto-généré —</option>
            ${idOptionsHtml}
          </select>
        </div>
      `;
    }

    // Section montant (si applicable)
    if (sheetType === "gl" || sheetType === "tb" || sheetType === "tb_n1") {
      const amountMode = mapping.amount_mode ?? "split";
      const labelSingle = sheetType === "gl" ? "Montant signé" : "Solde";
      const fieldSingle = sheetType === "gl" ? "montant" : "solde";

      html += `
        <div style="margin-top:10px; padding-top:10px; border-top: 1px solid var(--border)">
          <div class="mapping-field-label" style="margin-bottom:6px">Montant</div>
          <div class="mapping-amount-mode">
            <button type="button" class="mode-btn ${amountMode === "single" ? "active" : ""}" data-sheet="${sheetType}" data-mode="single">Une colonne</button>
            <button type="button" class="mode-btn ${amountMode === "split" ? "active" : ""}" data-sheet="${sheetType}" data-mode="split">Débit + Crédit</button>
          </div>

          ${amountMode === "single" ? `
            <div class="mapping-field">
              <span class="mapping-field-label">${labelSingle}</span>
              <select class="mapping-field-select ${(mapping as any)[fieldSingle] ? "" : "unset"}" data-sheet="${sheetType}" data-field="${fieldSingle}">
                <option value="">— sélectionner —</option>
                ${headers.map(h => `<option value="${h}" ${h === (mapping as any)[fieldSingle] ? "selected" : ""}>${h}</option>`).join("")}
              </select>
            </div>
          ` : `
            <div class="mapping-field">
              <span class="mapping-field-label">Débit</span>
              <select class="mapping-field-select ${mapping.debit ? "" : "unset"}" data-sheet="${sheetType}" data-field="debit">
                <option value="">— sélectionner —</option>
                ${headers.map(h => `<option value="${h}" ${h === mapping.debit ? "selected" : ""}>${h}</option>`).join("")}
              </select>
            </div>
            <div class="mapping-field">
              <span class="mapping-field-label">Crédit</span>
              <select class="mapping-field-select ${mapping.credit ? "" : "unset"}" data-sheet="${sheetType}" data-field="credit">
                <option value="">— sélectionner —</option>
                ${headers.map(h => `<option value="${h}" ${h === mapping.credit ? "selected" : ""}>${h}</option>`).join("")}
              </select>
            </div>
          `}
        </div>
      `;
    }

    body.innerHTML = html;

    bindMappingFieldEvents(sheetType);
  } catch (err: any) {
    body.innerHTML = `<p class="hint" style="color:var(--danger)">Erreur : ${err.message}</p>`;
  }
}

function getFieldLabel(field: string): string {
  const labels: Record<string, string> = {
    id: "Line ID",
    compte: "Compte",
    date: "Date",
    journal: "Journal",
    libelle: "Libellé",
    solde: "Solde",
    montant: "Montant",
    debit: "Débit",
    credit: "Crédit",
    financial_line_statutory: "Statutory",
    financial_line_abcotd: "ABCOTD",
    statement_type: "Type (BS/PL)",
  };
  return labels[field] ?? field;
}

function bindMappingFieldEvents(sheetType: "gl" | "tb" | "tb_n1" | "chart") {
  // Dropdowns de champs
  document.querySelectorAll<HTMLSelectElement>(`.mapping-field-select[data-sheet="${sheetType}"]`).forEach(sel => {
    sel.onchange = () => {
      const field = sel.dataset.field!;
      const value = sel.value;
      if (value) {
        (state.mappings[sheetType] as any)[field] = value;
      } else {
        delete (state.mappings[sheetType] as any)[field];
      }
      // Invalider le mapping (l'utilisateur doit le re-valider)
      state.mappingsValidated[sheetType] = false;
      updateMappingDot(sheetType);
      renderMappingBody(sheetType); // re-render pour mettre à jour les classes "unset"
    };
  });

  // Boutons mode montant (single / split)
  document.querySelectorAll<HTMLButtonElement>(`.mode-btn[data-sheet="${sheetType}"]`).forEach(btn => {
    btn.onclick = () => {
      const mode = btn.dataset.mode as "single" | "split";
      state.mappings[sheetType].amount_mode = mode;
      // Reset des champs incompatibles avec le nouveau mode
      if (mode === "single") {
        delete state.mappings[sheetType].debit;
        delete state.mappings[sheetType].credit;
      } else {
        delete state.mappings[sheetType].montant;
        delete state.mappings[sheetType].solde;
      }
      state.mappingsValidated[sheetType] = false;
      updateMappingDot(sheetType);
      renderMappingBody(sheetType);
    };
  });
}

function updateMappingDot(sheetType: "gl" | "tb" | "tb_n1" | "chart") {
  const dot = document.getElementById(`map-dot-${sheetType}`);
  if (!dot) return;
  if (state.mappingsValidated[sheetType]) {
    dot.classList.add("mapped");
  } else {
    dot.classList.remove("mapped");
  }
}

function bindMappingsApplyBtn() {
  const btn = document.getElementById("mappings-apply-btn") as HTMLButtonElement | null;
  if (!btn) return;
  btn.onclick = () => {
    const sheetTypes: ("gl" | "tb" | "tb_n1" | "chart")[] = ["gl", "tb", "tb_n1", "chart"];
    let appliedCount = 0;
    let incompleteCount = 0;
    for (const sheetType of sheetTypes) {
      const mapping = state.mappings[sheetType];
      if (mapping && isMappingComplete(mapping, sheetType)) {
        state.mappingsValidated[sheetType] = true;
        updateMappingDot(sheetType);
        appliedCount++;
      } else {
        incompleteCount++;
      }
    }
    refreshStepAvailability();
    refreshSetupAccordionSummaries();
    if (incompleteCount > 0) {
      console.log(`${appliedCount} mapping(s) appliqué(s), ${incompleteCount} incomplet(s)`);
    }
  };
}

function refreshAllMappingDots() {
  for (const sheetType of ["gl", "tb", "tb_n1", "chart"] as const) {
    updateMappingDot(sheetType);
  }
}

async function exportTestedPopulations() {
  const statusEl = document.getElementById("export-populations-status")!;
  const setExportStatus = (msg: string, kind: "info" | "success" | "error") => {
    statusEl.textContent = msg;
    statusEl.className = `status show ${kind}`;
  };

  setExportStatus("Détection des sections samplées...", "info");

  try {
    if (!state.sheets.gl) {
      throw new Error("Setup requis : la feuille GL doit être mappée.");
    }

    // Lecture des onglets existants pour identifier les Sample_*
    const sampledSections: string[] = await Excel.run(async (context) => {
      const sheets = context.workbook.worksheets;
      sheets.load("items/name");
      await context.sync();
      const names: string[] = [];
      for (const sh of sheets.items) {
        if (sh.name.startsWith("Sample_")) {
          // Extraire le nom de la section (ex: Sample_OPEX → OPEX)
          names.push(sh.name.substring("Sample_".length));
        }
      }
      return names;
    });

    if (sampledSections.length === 0) {
      throw new Error("Aucune section samplée trouvée. Lance d'abord le sampling.");
    }

    setExportStatus(`Lecture du GL et préparation des populations (${sampledSections.length} sections)...`, "info");

    // Lecture du GL
    const glLines = await readMappedSheet("gl");
    if (glLines.length === 0) throw new Error(`Feuille '${state.sheets.gl}' vide`);

    // Recopier la logique d'exclusion reversals
    let workingLines = glLines;
    if (reversalsState.excludeFromSampling) {
      const excludeIds = new Set<string>();
      try {
        await Excel.run(async (context) => {
          const sheets = context.workbook.worksheets;
          sheets.load("items/name");
          await context.sync();
          const exists = sheets.items.some((s: any) => s.name === "Reversals_Result");
          if (!exists) {
            console.warn("Reversals_Result non trouvé, pas d'exclusion reversals");
            return;
          }
          const sheet = context.workbook.worksheets.getItem("Reversals_Result");
          const usedRange = sheet.getUsedRange();
          usedRange.load(["values"]);
          await context.sync();
          const values = usedRange.values;

          // Trouver headers
          let headerRowIdx = -1;
          for (let r = 0; r < Math.min(values.length, 10); r++) {
            const row = values[r];
            if (row.includes("Decision") && row.includes("Line ID")) {
              headerRowIdx = r;
              break;
            }
          }
          if (headerRowIdx === -1) {
            console.warn("Headers introuvables dans Reversals_Result");
            return;
          }
          const headers = values[headerRowIdx];
          const lineIdColIdx = headers.indexOf("Line ID");
          const decisionColIdx = headers.indexOf("Decision");
          const bucketColIdx = headers.indexOf("Bucket");
          if (lineIdColIdx === -1 || decisionColIdx === -1) {
            console.warn("Reversals_Result n'a pas de colonne 'Decision' ou 'Line ID'. Lance 'Appliquer les décisions' d'abord.");
            return;
          }

          // Parcours 2 lignes par paire : la décision est sur la 1ère ligne (mergée)
          // On exclut les 2 lignes (original + reversal) si decision = "Reversal"
          const dataStartRow = headerRowIdx + 1;
          for (let i = dataStartRow; i < values.length; i += 2) {
            if (i + 1 >= values.length) break;
            const topRow = values[i];
            const bottomRow = values[i + 1];
            // Si on est sur le footer, on arrête
            if (!topRow[bucketColIdx] && !bottomRow[bucketColIdx]) break;
            const decision = String(topRow[decisionColIdx] || "").trim();
            if (decision === "Reversal") {
              const lineIdTop = String(topRow[lineIdColIdx] || "").trim();
              const lineIdBottom = String(bottomRow[lineIdColIdx] || "").trim();
              if (lineIdTop) excludeIds.add(lineIdTop);
              if (lineIdBottom) excludeIds.add(lineIdBottom);
            }
          }
        });
      } catch (err) {
        console.warn("Reversals_Result non trouvé, pas d'exclusion reversals");
      }
      workingLines = workingLines.filter((l: any) => !excludeIds.has(String(l["id"] ?? l["ligne_id"] ?? "")));
    }

    // Filtre opening journals
    const openingJournals = getSelectedJournals("recon-journals");
    if (openingJournals.length > 0) {
      workingLines = workingLines.filter((l: any) => !openingJournals.includes(String(l["journal"] ?? "")));
    }

    // Lecture des comptes par section depuis samplingState
    const sectionAccountsMap = new Map<string, string[]>();
    for (const sec of samplingState.sections) {
      const includedAccounts = sec.accounts.filter(a => !sec.excluded_accounts.has(a));
      sectionAccountsMap.set(sec.name, includedAccounts);
    }

    let createdCount = 0;
    setExportStatus(`Écriture des onglets Population_*...`, "info");

    await Excel.run(async (context) => {
      const sheets = context.workbook.worksheets;
      sheets.load("items/name");
      await context.sync();

      for (const sectionName of sampledSections) {
        const accounts = sectionAccountsMap.get(sectionName);
        if (!accounts || accounts.length === 0) {
          continue; // Section pas trouvée dans samplingState (peut-être un sample ancien)
        }

        const accountSet = new Set(accounts);
        const sectionLines = workingLines.filter((l: any) => accountSet.has(String(l["compte"] ?? "")));

        if (sectionLines.length === 0) continue;

        const popSheetName = `Population_${sectionName}`.substring(0, 31);

        // Supprimer si existe
        const existing = sheets.items.find((sh: any) => sh.name === popSheetName);
        if (existing) {
          existing.delete();
          await context.sync();
        }

        const sheet = sheets.add(popSheetName);

        // Titre
        const titleRange = sheet.getRange("A1");
        titleRange.values = [[`Population testée — ${formatLabel(sectionName)}`]];
        titleRange.format.font.bold = true;
        titleRange.format.font.size = 14;

        // Sous-titre
        const subtitleRange = sheet.getRange("A2");
        subtitleRange.values = [[`${sectionLines.length} lignes · ${accounts.length} comptes inclus · Filtres appliqués (opening journals, reversals exclus si activé)`]];
        subtitleRange.format.font.italic = true;
        subtitleRange.format.font.color = "#52525B";

        // Headers
        const headers = Object.keys(sectionLines[0]);
        const headerRange = sheet.getRangeByIndexes(2, 0, 1, headers.length);
        headerRange.values = [headers];
        headerRange.format.font.bold = true;
        headerRange.format.fill.color = "#1E40AF";
        headerRange.format.font.color = "white";

        // Data
        const dataValues = sectionLines.map((row: any) =>
          headers.map((h) => {
            const v = row[h];
            if (typeof v === "boolean") return v ? "TRUE" : "FALSE";
            return v ?? "";
          })
        );
        const dataRange = sheet.getRangeByIndexes(3, 0, dataValues.length, headers.length);
        dataRange.values = dataValues;

        await applyNumberFormatsToResultSheet(sheet, sectionLines, 3);
        sheet.getUsedRange().format.autofitColumns();
        await context.sync();

        createdCount++;
      }
    });

    setExportStatus(`✓ ${createdCount} onglets Population_* créés`, "success");
  } catch (err: any) {
    console.error(err);
    setExportStatus(`Erreur : ${err.message}`, "error");
  }
}

function bindReversalsExclusion() {
  // Exclusion toujours active (toggle retiré pour simplifier l'UX)
  reversalsState.excludeFromSampling = true;
}

async function syncPairDecisionToExcel(originalId: any, reversalId: any, newDecision: string): Promise<void> {
  await Excel.run(async (context) => {
    const sheets = context.workbook.worksheets;
    sheets.load("items/name");
    await context.sync();
    const exists = sheets.items.some((s: any) => s.name === "Reversals_Result");
    if (!exists) return;

    const sheet = context.workbook.worksheets.getItem("Reversals_Result");
    const usedRange = sheet.getUsedRange();
    usedRange.load("values");
    await context.sync();

    const allValues = usedRange.values;
    if (allValues.length < 5) return;

    let headerRowIdx = -1;
    for (let i = 0; i < Math.min(10, allValues.length); i++) {
      const row = allValues[i];
      if (row.includes("Decision") && row.includes("Line ID")) {
        headerRowIdx = i;
        break;
      }
    }
    if (headerRowIdx === -1) return;

    const headers = allValues[headerRowIdx];
    const lineIdColIdx = headers.indexOf("Line ID");
    const decisionColIdx = headers.indexOf("Decision");
    const bucketColIdx = headers.indexOf("Bucket");
    if (lineIdColIdx === -1 || decisionColIdx === -1) return;

    const dataStartRow = headerRowIdx + 1;
    const targetIds = [String(originalId), String(reversalId)];
    for (let i = dataStartRow; i < allValues.length; i += 2) {
      if (i + 1 >= allValues.length) break;
      const topRow = allValues[i];
      const bottomRow = allValues[i + 1];
      if (!topRow[bucketColIdx] && !bottomRow[bucketColIdx]) break;
      const idTop = String(topRow[lineIdColIdx] || "").trim();
      const idBottom = String(bottomRow[lineIdColIdx] || "").trim();
      if (targetIds.includes(idTop) && targetIds.includes(idBottom)) {
        const decisionCell = sheet.getRangeByIndexes(i, decisionColIdx, 1, 1);
        decisionCell.values = [[newDecision]];
        if (newDecision === "Reversal") {
          decisionCell.format.fill.color = "#D1FAE5";
          decisionCell.format.font.color = "#065F46";
          decisionCell.format.font.bold = true;
        } else if (newDecision === "Not reversal") {
          decisionCell.format.fill.color = "#FEE2E2";
          decisionCell.format.font.color = "#B91C1C";
          decisionCell.format.font.bold = true;
        }
        break;
      }
    }
    await context.sync();
  });
}

function renderProbableValidation() {
  const block = document.getElementById("rev-probable-block")!;
  const cardbox = document.getElementById("rev-cardbox")!;
  const counter = document.getElementById("rev-card-counter")!;
  const prevBtn = document.getElementById("rev-card-prev") as HTMLButtonElement;
  const nextBtn = document.getElementById("rev-card-next") as HTMLButtonElement;

  // Toujours afficher l'exclusion si on a des CERTAIN ou PROBABLE
  const hasReversals = reversalsState.certainIds.size > 0 || reversalsState.probablePairs.length > 0;
  if (reversalsState.probablePairs.length === 0) {
    block.style.display = "none";
    return;
  }

  block.style.display = "block";

  function renderCard() {
    const idx = reversalsState.probableIdx;
    const total = reversalsState.probablePairs.length;
    const pair = reversalsState.probablePairs[idx];

    counter.textContent = `${idx + 1} / ${total}`;
    prevBtn.disabled = idx === 0;
    nextBtn.disabled = idx === total - 1;

    const validatedCount = reversalsState.probablePairs.filter(p => p.validation === "validated").length;
    const invalidatedCount = reversalsState.probablePairs.filter(p => p.validation === "invalidated").length;
    
    function renderRecap() {
      const recapEl = document.getElementById("rev-validation-recap")!;
      const certainCount = reversalsState.certainIds.size / 2; // chaque paire = 2 ids
      const validated = reversalsState.probablePairs.filter(p => p.validation === "validated").length;
      const invalidated = reversalsState.probablePairs.filter(p => p.validation === "invalidated").length;
      const pending = reversalsState.probablePairs.filter(p => p.validation === "pending").length;

      recapEl.innerHTML = `
        <div class="recap-item recap-certain">
          <span class="recap-label">CERTAIN auto-exclus</span>
          <span class="recap-value">${certainCount}</span>
        </div>
        <div class="recap-item recap-validated">
          <span class="recap-label">PROBABLE validés (exclus)</span>
          <span class="recap-value">${validated}</span>
        </div>
        <div class="recap-item recap-invalidated">
          <span class="recap-label">PROBABLE invalidés (gardés)</span>
          <span class="recap-value">${invalidated}</span>
        </div>
        <div class="recap-item recap-pending">
          <span class="recap-label">PROBABLE pending (gardés)</span>
          <span class="recap-value">${pending}</span>
        </div>
      `;
    }

    cardbox.innerHTML = `
      <div class="card-row">
        <span class="k">Compte</span>
        <span class="v">${pair.compte ?? "—"}</span>
      </div>
      <div class="card-row">
        <span class="k">Score</span>
        <span class="v">${pair.score ?? "—"}</span>
      </div>
      <div class="card-row">
        <span class="k">Délai</span>
        <span class="v">${pair.days_diff ?? "—"} jours</span>
      </div>

      <div class="card-pair">
        <div class="card-pair-side">
          <span class="card-pair-label">Original</span>
          <span class="card-pair-value">${formatEur(parseFloat(pair.montant) || 0)}</span>
          <span class="card-pair-value" style="font-size:10px;color:var(--text-subtle)">${pair.date_original ?? ""}</span>
          <span class="card-pair-value" style="font-size:10px">${pair.libelle_original ?? ""}</span>
        </div>
        <div class="card-pair-side">
          <span class="card-pair-label">Reversal</span>
          <span class="card-pair-value">${formatEur(-(parseFloat(pair.montant) || 0))}</span>
          <span class="card-pair-value" style="font-size:10px;color:var(--text-subtle)">${pair.date_reversal ?? ""}</span>
          <span class="card-pair-value" style="font-size:10px">${pair.libelle_reversal ?? ""}</span>
        </div>
      </div>

      <div class="card-actions">
        <button type="button" class="btn-validate ${pair.validation === "validated" ? "active" : ""}" data-action="validated">
          ✓ Valider
        </button>
        <button type="button" class="btn-invalidate ${pair.validation === "invalidated" ? "active" : ""}" data-action="invalidated">
          ✗ Invalider
        </button>
      </div>

      <div class="card-progress">
        <div class="card-progress-bar" style="width:${((validatedCount + invalidatedCount) / total * 100).toFixed(0)}%"></div>
      </div>
    `;
    renderRecap();

    cardbox.querySelectorAll<HTMLButtonElement>(".card-actions button").forEach(btn => {
      btn.onclick = async () => {
        const action = btn.dataset.action;
        if (action === "validated" || action === "invalidated") {
          pair.validation = action;
          // Sync immédiate vers Excel : mettre à jour la cellule Decision
          const newDecision = action === "validated" ? "Reversal" : "Not reversal";
          try {
            await syncPairDecisionToExcel(pair.original_id, pair.reversal_id, newDecision);
          } catch (err: any) {
            console.warn("Sync Excel échoué :", err.message);
          }
        }
        // Auto-avancer si pas la dernière
        if (idx < total - 1) {
          reversalsState.probableIdx = idx + 1;
        }
        renderCard();
      };
    });
  }

  prevBtn.onclick = () => {
    if (reversalsState.probableIdx > 0) {
      reversalsState.probableIdx--;
      renderCard();
    }
  };
  nextBtn.onclick = () => {
    if (reversalsState.probableIdx < reversalsState.probablePairs.length - 1) {
      reversalsState.probableIdx++;
      renderCard();
    }
  };

  renderCard();
}

async function updateReversalsSummary() {
  const countEl = document.getElementById("rev-sections-count")!;
  const linesEl = document.getElementById("rev-lines-count")!;

  const checkedCount = reversalsState.sections.filter(s => s.checked).length;
  countEl.textContent = String(checkedCount);

  // Calcul du nb de lignes GL impactées (peut être lent sur gros GL)
  if (!state.sheets.gl) {
    linesEl.textContent = "?";
    return;
  }

  try {
    const accountsSet = new Set<string>();
    for (const sec of reversalsState.sections) {
      if (sec.checked) sec.accounts.forEach(a => accountsSet.add(a));
    }

    if (accountsSet.size === 0) {
      linesEl.textContent = "0";
      return;
    }

    const glLines = await readMappedSheet("gl");
    const filteredCount = glLines.filter((l: any) => accountsSet.has(String(l["compte"]))).length;
    linesEl.textContent = filteredCount.toLocaleString("en-US");
  } catch {
    linesEl.textContent = "?";
  }
}

async function buildJournalMultiselect(containerId: string, instanceId: string, defaults: string[] = ["OUV", "AN"]) {
  const container = document.getElementById(containerId);
  if (!container) return;

  const journals = await detectJournals();

  // Init selection : pré-coche les defaults qui existent dans la liste
  if (!journalSelect.selections.has(instanceId)) {
    const initial = new Set<string>();
    for (const def of defaults) {
      if (journals.includes(def)) initial.add(def);
    }
    journalSelect.selections.set(instanceId, initial);
  }

  const selected = journalSelect.selections.get(instanceId)!;

  function renderTrigger() {
    const sel = journalSelect.selections.get(instanceId)!;
    const arr = Array.from(sel);
    if (arr.length === 0) return `<span class="multiselect-empty-text">Aucun (pas de filtre)</span>`;
    if (arr.length <= 3) return arr.join(", ");
    return `${arr.length} journaux sélectionnés`;
  }

  function render() {
    if (journals.length === 0) {
      container.innerHTML = `<div class="multiselect-empty">Aucun journal détecté dans le GL</div>`;
      return;
    }
    container.innerHTML = `
      <div class="multiselect" id="${instanceId}-wrap">
        <button type="button" class="multiselect-trigger" id="${instanceId}-trigger">${renderTrigger()}</button>
        <div class="multiselect-panel">
          ${journals.map(j => {
            const checked = selected.has(j) ? "checked" : "";
            return `
              <label class="multiselect-option">
                <input type="checkbox" data-journal="${j}" ${checked}>
                <span class="label-text">${j}</span>
              </label>
            `;
          }).join("")}
        </div>
      </div>
    `;

    const wrap = document.getElementById(`${instanceId}-wrap`)!;
    const trigger = document.getElementById(`${instanceId}-trigger`)!;
    trigger.onclick = (e) => {
      e.stopPropagation();
      // Ferme tous les autres
      document.querySelectorAll(".multiselect.open").forEach(el => {
        if (el !== wrap) el.classList.remove("open");
      });
      wrap.classList.toggle("open");
    };

    wrap.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach(cb => {
      cb.onchange = () => {
        const j = cb.dataset.journal!;
        const sel = journalSelect.selections.get(instanceId)!;
        if (cb.checked) sel.add(j); else sel.delete(j);
        trigger.innerHTML = renderTrigger();
      };
    });
  }

  render();
}

// Ferme tous les dropdowns au clic ailleurs
document.addEventListener("click", () => {
  document.querySelectorAll(".multiselect.open").forEach(el => el.classList.remove("open"));
});

function getSelectedJournals(instanceId: string): string[] {
  const set = journalSelect.selections.get(instanceId);
  return set ? Array.from(set) : [];
}

// État Reversals (sections in-scope cochées + comptes correspondants)
// État Scoping override
const scopingState = {
  rows: [] as any[],
  decisions: new Map<string, { autoInScope: boolean; userInScope: boolean }>(),
  showOutOfScope: false,
  loaded: false,
};

const reversalsState = {
  sections: [] as { name: string; accounts: string[]; amount: number; checked: boolean }[],
  loaded: false,
  probablePairs: [] as any[],
  probableIdx: 0,
  excludeFromSampling: false,
  certainIds: new Set<string>(),
};

function getPM(): number { return state.materiality * state.pmPct / 100; }
function getCTT(): number { return state.materiality * state.cttPct / 100; }

function formatEur(n: number): string {
  if (n === null || n === undefined || !isFinite(n)) return "€0";

  const sign = n < 0 ? "-" : "";
  const abs = Math.abs(n);

  if (abs >= 1_000_000) {
    // Millions : 1 décimale, ex €10.5M, €-11.4M
    return `€${sign}${(abs / 1_000_000).toFixed(1)}M`;
  }
  if (abs >= 1000) {
    // Milliers : pas de décimale, ex €750k, €-4192k
    return `€${sign}${Math.round(abs / 1000)}k`;
  }
  // Centaines et moins : montant complet sans décimale
  return `€${sign}${Math.round(abs)}`;
}

function formatLabel(s: string | null | undefined): string {
  if (!s) return "";
  return String(s).replace(/_/g, " ");
}

function applyNumberFormatToColumn(sheet: any, colIdx: number, startRow: number, numRows: number, isInteger: boolean = false) {
  if (numRows === 0) return;
  const range = sheet.getRangeByIndexes(startRow, colIdx, numRows, 1);
  range.numberFormat = isInteger ? [["#,##0"]] : [["#,##0.00"]];
}

function isNumericColumn(rows: any[], colName: string): { isNumeric: boolean; isInteger: boolean } {
  let numericCount = 0;
  let nonIntegerCount = 0;
  let totalNonNull = 0;
  for (const row of rows.slice(0, Math.min(rows.length, 50))) {
    const v = row[colName];
    if (v === null || v === undefined || v === "") continue;
    totalNonNull++;
    const num = parseFloat(String(v).replace(/[,\s]/g, ""));
    if (isFinite(num) && !isNaN(num)) {
      numericCount++;
      if (num !== Math.round(num)) nonIntegerCount++;
    }
  }
  if (totalNonNull === 0) return { isNumeric: false, isInteger: false };
  return {
    isNumeric: numericCount / totalNonNull > 0.7,
    isInteger: nonIntegerCount === 0,
  };
}

async function applyNumberFormatsToResultSheet(sheet: any, rows: any[], headerOffset: number) {
  if (rows.length === 0) return;
  const headers = Object.keys(rows[0]);
  for (let i = 0; i < headers.length; i++) {
    const colInfo = isNumericColumn(rows, headers[i]);
    if (colInfo.isNumeric) {
      applyNumberFormatToColumn(sheet, i, headerOffset, rows.length, colInfo.isInteger);
    }
  }
}

function formatNumberInput(n: number): string {
  if (!isFinite(n)) return "0";
  return Math.round(n).toLocaleString("en-US");
}

function parseNumberInput(s: string): number {
  return parseFloat(s.replace(/[^0-9.-]/g, "")) || 0;
}

// ============================================================
// COLUMN MAPPING
// ============================================================

const COLUMN_ALIASES: Record<string, string[]> = {
  // GL fields
  id: ["id", "ligne", "row_id", "transaction_id", "n_ecriture", "ecriture", "doc_id", "line_id"],
  compte: ["compte", "account", "account_number", "compte_general", "n_compte", "numero_compte", "comptegeneral", "acct"],
  date: ["date", "date_ecriture", "date_operation", "transaction_date", "date_op", "booking_date", "date_doc"],
  journal: ["journal", "journal_code", "code_journal", "type_journal", "jrn", "jnl"],
  libelle: ["libelle", "description", "label", "intitule", "comment", "narration", "memo", "comm"],
  montant: ["montant", "amount", "value", "valeur", "net", "net_amount"],
  debit: ["debit", "debet", "debit_amount", "amount_debit", "dr"],
  credit: ["credit", "credit_amount", "amount_credit", "cr"],
  // TB fields
  solde: ["solde", "balance", "ending_balance", "closing", "net_balance"],
  closing_debit: ["closing_debit", "debit_closing", "debit_balance", "ending_debit"],
  closing_credit: ["closing_credit", "credit_closing", "credit_balance", "ending_credit"],
  // Chart fields
  financial_line_statutory: ["financial_line_statutory", "statutory", "fl_statutory", "line_statutory", "fs_line"],
  financial_line_abcotd: ["financial_line_abcotd", "abcotd", "fl_abcotd", "line_abcotd"],
  statement_type: ["statement_type", "statement", "type", "stmt_type", "bs_pl"],
};

function normalizeHeaderName(s: string): string {
  return s.toLowerCase().trim().replace(/[\s\-]+/g, "_");
}

function findMatchingHeader(headers: string[], targetField: string): string | null {
  const normalizedHeaders = headers.map(normalizeHeaderName);
  const aliases = COLUMN_ALIASES[targetField] ?? [targetField];
  for (const alias of aliases) {
    const idx = normalizedHeaders.indexOf(normalizeHeaderName(alias));
    if (idx !== -1) return headers[idx];
  }
  return null;
}

function getRequiredFieldsForSheet(sheetType: "gl" | "tb" | "tb_n1" | "chart"): { core: string[]; amount: "split_or_single" | "single_solde_or_split" } {
  if (sheetType === "gl") {
    return { core: ["compte", "date", "journal", "libelle"], amount: "split_or_single" };
  } else if (sheetType === "tb" || sheetType === "tb_n1") {
    return { core: ["compte"], amount: "single_solde_or_split" };
  } else {
    return { core: ["compte", "financial_line_statutory", "financial_line_abcotd", "statement_type"], amount: "split_or_single" };
  }
}

function autoDetectMapping(headers: string[], sheetType: "gl" | "tb" | "tb_n1" | "chart"): SheetMapping {
  const mapping: SheetMapping = {};
  const reqs = getRequiredFieldsForSheet(sheetType);

  // Champs core
  for (const field of reqs.core) {
    const match = findMatchingHeader(headers, field);
    if (match) (mapping as any)[field] = match;
  }

  // Détection du mode montant
  if (sheetType === "gl") {
    const debitMatch = findMatchingHeader(headers, "debit");
    const creditMatch = findMatchingHeader(headers, "credit");
    const montantMatch = findMatchingHeader(headers, "montant");
    if (debitMatch && creditMatch) {
      mapping.amount_mode = "split";
      mapping.debit = debitMatch;
      mapping.credit = creditMatch;
    } else if (montantMatch) {
      mapping.amount_mode = "single";
      mapping.montant = montantMatch;
    } else {
      mapping.amount_mode = "split";
    }
  } else if (sheetType === "tb" || sheetType === "tb_n1") {
    const soldeMatch = findMatchingHeader(headers, "solde");
    const closingDebitMatch = findMatchingHeader(headers, "closing_debit");
    const closingCreditMatch = findMatchingHeader(headers, "closing_credit");
    const debitMatch = findMatchingHeader(headers, "debit");
    const creditMatch = findMatchingHeader(headers, "credit");
    if (soldeMatch) {
      mapping.amount_mode = "single";
      mapping.solde = soldeMatch;
    } else if (closingDebitMatch && closingCreditMatch) {
      mapping.amount_mode = "split";
      mapping.debit = closingDebitMatch;
      mapping.credit = closingCreditMatch;
    } else if (debitMatch && creditMatch) {
      mapping.amount_mode = "split";
      mapping.debit = debitMatch;
      mapping.credit = creditMatch;
    } else {
      mapping.amount_mode = "single";
    }
  }

  return mapping;
}

function applyMapping(rows: any[], mapping: SheetMapping, sheetType: "gl" | "tb" | "tb_n1" | "chart"): any[] {
  if (rows.length === 0) return rows;
  return rows.map(row => {
    const newRow: any = { ...row }; // garde toutes les colonnes originales

    // Renommer les champs core selon le mapping
    if (mapping.id && row[mapping.id] !== undefined) newRow["id"] = row[mapping.id];
    if (mapping.compte && row[mapping.compte] !== undefined) newRow["compte"] = row[mapping.compte];
    if (mapping.date && row[mapping.date] !== undefined) newRow["date"] = row[mapping.date];
    if (mapping.journal && row[mapping.journal] !== undefined) newRow["journal"] = row[mapping.journal];
    if (mapping.libelle && row[mapping.libelle] !== undefined) newRow["libelle"] = row[mapping.libelle];

    // Mode montant
    if (mapping.amount_mode === "single") {
      if (sheetType === "gl" && mapping.montant && row[mapping.montant] !== undefined) {
        newRow["montant"] = row[mapping.montant];
        // Pour le GL en mode single, on dérive aussi debit/credit pour compatibilité backend
        const v = parseFloat(row[mapping.montant]);
        if (!isNaN(v)) {
          newRow["debit"] = v >= 0 ? v : 0;
          newRow["credit"] = v < 0 ? -v : 0;
        }
      } else if ((sheetType === "tb" || sheetType === "tb_n1") && mapping.solde && row[mapping.solde] !== undefined) {
        newRow["solde"] = row[mapping.solde];
      }
    } else if (mapping.amount_mode === "split") {
      if (mapping.debit && row[mapping.debit] !== undefined) newRow["debit"] = row[mapping.debit];
      if (mapping.credit && row[mapping.credit] !== undefined) newRow["credit"] = row[mapping.credit];
    }

    // Chart fields
    if (mapping.financial_line_statutory && row[mapping.financial_line_statutory] !== undefined) {
      newRow["financial_line_statutory"] = row[mapping.financial_line_statutory];
    }
    if (mapping.financial_line_abcotd && row[mapping.financial_line_abcotd] !== undefined) {
      newRow["financial_line_abcotd"] = row[mapping.financial_line_abcotd];
    }
    if (mapping.statement_type && row[mapping.statement_type] !== undefined) {
      newRow["statement_type"] = row[mapping.statement_type];
    }

    return newRow;
  });
}

function isMappingComplete(mapping: SheetMapping, sheetType: "gl" | "tb" | "tb_n1" | "chart"): boolean {
  const reqs = getRequiredFieldsForSheet(sheetType);
  for (const field of reqs.core) {
    if (!(mapping as any)[field]) return false;
  }
  if (mapping.amount_mode === "single") {
    if (sheetType === "gl" && !mapping.montant) return false;
    if ((sheetType === "tb" || sheetType === "tb_n1") && !mapping.solde) return false;
  } else if (mapping.amount_mode === "split") {
    if (!mapping.debit || !mapping.credit) return false;
  }
  return true;
}

async function readSheetHeaders(sheetName: string): Promise<string[]> {
  return await Excel.run(async (context) => {
    const sheet = context.workbook.worksheets.getItem(sheetName);
    const usedRange = sheet.getUsedRange();
    usedRange.load(["values", "rowCount", "columnCount"]);
    await context.sync();
    if (usedRange.rowCount === 0) return [];
    return (usedRange.values[0] as any[]).map((h: any) => String(h));
  });
}

function refreshMatStrip() {
  document.getElementById("mat-display")!.textContent = formatEur(state.materiality);
  document.getElementById("pm-display")!.textContent = formatEur(getPM());
  document.getElementById("ctt-display")!.textContent = formatEur(getCTT());
}

// ============================================================
// NAV
// ============================================================
function showScreen(name: string) {
  const targetBtn = document.querySelector<HTMLElement>(`.step-btn[data-screen="${name}"]`);
  if (targetBtn?.classList.contains("disabled")) {
    return;
  }
  document.querySelectorAll<HTMLElement>(".screen").forEach(s => s.classList.remove("active"));
  document.querySelector<HTMLElement>(`.screen[data-screen="${name}"]`)!.classList.add("active");
  document.querySelectorAll<HTMLElement>(".step-btn").forEach(b => b.classList.remove("active"));
  document.querySelector<HTMLElement>(`.step-btn[data-screen="${name}"]`)!.classList.add("active");
// Init des composants par écran
  if (name === "recon") {
    buildJournalMultiselect("recon-journals-container", "recon-journals");
  }
  if (name === "scoping") {
    bindScopingControls();
    bindScopingReviewControls();
    renderScopingReview();
  }
  if (name === "analytical") {
    bindAnalyticalControls();
  }
  if (name === "reversals") {
    loadReversalsSections();
    bindReversalsExclusion();
  }
}

// ============================================================
// SETUP SCREEN
// ============================================================
function bindSetupAccordions() {
  document.querySelectorAll<HTMLElement>(".setup-accordion-header").forEach(header => {
    header.onclick = () => {
      const target = header.dataset.target!;
      const accordion = header.closest(".setup-accordion") as HTMLElement;
      const body = document.getElementById(`setup-body-${target}`)!;
      const arrow = header.querySelector(".setup-arrow") as HTMLElement;
      const isOpen = accordion.classList.contains("expanded");

      if (isOpen) {
        accordion.classList.remove("expanded");
        body.style.display = "none";
        arrow.textContent = "▶";
      } else {
        accordion.classList.add("expanded");
        body.style.display = "block";
        arrow.textContent = "▼";
      }
    };
  });
}

function refreshSetupAccordionSummaries() {
  // Données : valide si GL est présent (TB, Chart, TB_N1 sont optionnels)
  const dataDot = document.getElementById("setup-dot-data");
  if (dataDot) {
    const dataValid = !!state.sheets.gl;
    dataDot.classList.toggle("validated", dataValid);
  }

  // Mappings : valide si toutes les feuilles présentes ont un mapping validé
  const mappingsDot = document.getElementById("setup-dot-mappings");
  if (mappingsDot) {
    const sheetsList: ("gl" | "tb" | "tb_n1" | "chart")[] = ["gl", "tb", "tb_n1", "chart"];
    // GL est obligatoire et toujours validé via mapping
    const glOk = !!state.sheets.gl && state.mappingsValidated.gl;
    // Pour les autres feuilles : si présente (truthy), son mapping doit être validé
    const tbOk = !state.sheets.tb || state.mappingsValidated.tb;
    const tbN1Ok = !state.sheets.tb_n1 || state.mappingsValidated.tb_n1;
    const chartOk = !state.sheets.chart || state.mappingsValidated.chart;
    const allValid = glOk && tbOk && tbN1Ok && chartOk;

    // Debug
    mappingsDot.classList.toggle("validated", allValid);
  }

  // Matérialité : valide si tous les champs ont des valeurs cohérentes
  const materialityDot = document.getElementById("setup-dot-materiality");
  if (materialityDot) {
    const matValid = state.materiality > 0 && state.pmPct > 0 && state.cttPct > 0;
    materialityDot.classList.toggle("validated", matValid);
  }
}
function bindSetupScreen() {
  const matInput = document.getElementById("mat-input") as HTMLInputElement;
  const pmInput = document.getElementById("pm-input") as HTMLInputElement;
  const cttInput = document.getElementById("ctt-input") as HTMLInputElement;
  

  // Met à jour TOUS les affichages (top strip + section Matérialité)
  function refreshAllMatDisplays() {
    refreshMatStrip();
    refreshSetupAccordionSummaries();
  }

  matInput.oninput = () => {
    state.materiality = parseNumberInput(matInput.value);
    refreshAllMatDisplays();
  };
  matInput.onblur = () => {
    matInput.value = formatNumberInput(state.materiality);
  };

  pmInput.oninput = () => {
    const v = parseFloat(pmInput.value);
    if (isNaN(v) || v < 0 || v > 100) return;
    state.pmPct = v;
    refreshAllMatDisplays();
  };
  cttInput.oninput = () => {
    const v = parseFloat(cttInput.value);
    if (isNaN(v) || v < 0 || v > 100) return;
    state.cttPct = v;
    refreshAllMatDisplays();
  };

  document.getElementById("rescan-btn")!.onclick = scanAndDetectSheets;
  document.getElementById("validate-btn")!.onclick = validateSetup;
  bindMappingAccordions();
  bindSetupAccordions();
  bindMappingsApplyBtn();
  // Initial format pour l'input materiality
  matInput.value = formatNumberInput(state.materiality);
  refreshAllMatDisplays();
  refreshSetupAccordionSummaries();
  scanAndDetectSheets();
}

function guessRole(sheetName: string): "gl" | "tb" | "chart" | "tb_n1" | null {
  const n = sheetName.toLowerCase().trim();
  // TB N-1 : "TB_N1", "TB N-1", "Balance N-1", "Trial Balance prior", etc.
  if (n === "tb_n1" || n === "tbn1" || n === "tb-n1" || n === "tb n1" || n === "tb n-1") return "tb_n1";
  if (n.match(/(tb|balance|bal|trial).*(n-?1|prior|py|previous|précédent)/i)) return "tb_n1";
  if (n.match(/(n-?1|prior|py|previous).*(tb|balance|bal|trial)/i)) return "tb_n1";
  // Chart of accounts
  if (n.match(/(chart|plan|coa|comptes?$)/i)) return "chart";
  // TB courante
  if (n === "tb" || n.match(/^(tb|balance|bal|trial)$/i) || n.match(/trial.*balance/i) || n.match(/balance.*comptable/i)) return "tb";
  // GL
  if (n.match(/^(gl|ledger|journal)$/i) || n.match(/^gl[-_ ]/i) || n.match(/general.*ledger/i) || n === "gl") return "gl";
  return null;
}

async function autoGenerateChartBegaap() {
  // Pas de status local, utilise le status général du Setup
  const setStatus = (msg: string, kind: "info" | "success" | "error") => {
    setSetupStatus(msg, kind);
  };

  // Vérifier qu'on a une TB sélectionnée
  if (!state.sheets.tb) {
    setStatus("Sélectionne d'abord une feuille TB pour pouvoir auto-générer le Chart.", "error");
    return;
  }

  // Vérifier que le mapping TB est validé (sinon on ne peut pas lire les comptes)
  if (!state.mappingsValidated.tb) {
    setStatus("Valide d'abord le mapping de la TB (déplie l'accordéon Trial Balance).", "error");
    return;
  }

  try {
    // Lire la TB avec mapping appliqué (donc colonnes standardisées)
    const tbRows = await readMappedSheet("tb");
    if (tbRows.length === 0) {
      throw new Error("La TB est vide");
    }

    // Extraire les comptes uniques + libellés (s'il y a une colonne libelle_compte ou libelle)
    const accountsSet = new Set<string>();
    const libelles: Record<string, string> = {};
    for (const row of tbRows) {
      const compte = String(row["compte"] ?? "").trim();
      if (!compte) continue;
      accountsSet.add(compte);
      const libelle = String(row["libelle_compte"] ?? row["libelle"] ?? "").trim();
      if (libelle && !libelles[compte]) {
        libelles[compte] = libelle;
      }
    }

    const accountsList = Array.from(accountsSet);
    if (accountsList.length === 0) {
      throw new Error("Aucun compte trouvé dans la TB");
    }

    setStatus(`${accountsList.length} comptes détectés. Génération du Chart BEGAAP...`, "info");

    // Appeler le backend
    const response = await fetch(`${BACKEND_URL}/auto-chart-begaap`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        accounts: accountsList,
        libelles: libelles,
      }),
    });

    if (!response.ok) {
      const err = await response.text();
      throw new Error(`Backend ${response.status}: ${err}`);
    }

    const result = await response.json();
    const chartRows = result.chart;
    const summary = result.summary;

    setStatus(`Création de l'onglet Chart_Auto...`, "info");

    // Créer l'onglet Chart_Auto
    await Excel.run(async (context) => {
      const sheets = context.workbook.worksheets;
      sheets.load("items/name");
      await context.sync();

      // Supprimer l'onglet existant si présent
      const existing = sheets.items.find((s: any) => s.name === "Chart_Auto");
      if (existing) {
        existing.delete();
        await context.sync();
      }

      const sheet = sheets.add("Chart_Auto");

      // Headers
      const headers = ["compte", "libelle_compte", "financial_line_statutory", "financial_line_abcotd", "statement_type"];
      const headerRange = sheet.getRangeByIndexes(0, 0, 1, headers.length);
      headerRange.values = [headers];
      headerRange.format.font.bold = true;
      headerRange.format.fill.color = "#1E40AF";
      headerRange.format.font.color = "white";

      // Data
      const dataValues = chartRows.map((row: any) => [
        row.compte,
        row.libelle_compte,
        row.financial_line_statutory,
        row.financial_line_abcotd,
        row.statement_type,
      ]);
      const dataRange = sheet.getRangeByIndexes(1, 0, dataValues.length, headers.length);
      dataRange.values = dataValues;

      sheet.getUsedRange().format.autofitColumns();
      await context.sync();
    });

    // Mettre à jour state.sheets.chart vers Chart_Auto
    state.sheets.chart = "Chart_Auto";

    // Mettre à jour la dropdown map-chart
    const chartDropdown = document.getElementById("map-chart") as HTMLSelectElement;
    // Vérifier si l'option Chart_Auto existe déjà, sinon l'ajouter
    let chartAutoOption = Array.from(chartDropdown.options).find(o => o.value === "Chart_Auto");
    if (!chartAutoOption) {
      const newOption = document.createElement("option");
      newOption.value = "Chart_Auto";
      newOption.text = "Chart_Auto (auto-généré)";
      chartDropdown.appendChild(newOption);
    }
    chartDropdown.value = "Chart_Auto";

    // Auto-mapping du Chart (les colonnes sont les noms standards)
    state.mappings.chart = {
      compte: "compte",
      financial_line_statutory: "financial_line_statutory",
      financial_line_abcotd: "financial_line_abcotd",
      statement_type: "statement_type",
    };
    state.mappingsValidated.chart = true;
    updateMappingDot("chart");
    refreshStepAvailability();

    // Status success avec les stats
    let msg = `✓ Chart_Auto créé : ${summary.classified_count} classifiés / ${summary.total_accounts} comptes`;
    if (summary.unclassified_count > 0) {
      msg += ` (${summary.unclassified_count} non classifiés : ${summary.unclassified_accounts.slice(0, 5).join(", ")}${summary.unclassified_count > 5 ? "..." : ""})`;
    }
    setStatus(msg, summary.unclassified_count > 0 ? "info" : "success");

  } catch (err: any) {
    console.error(err);
    setStatus(`Erreur : ${err.message}`, "error");
  } finally {
  }
}
async function scanAndDetectSheets() {
  const btn = document.getElementById("rescan-btn") as HTMLButtonElement;
  btn.disabled = true;

  try {
    const allSheets = await Excel.run(async (context) => {
      const sheets = context.workbook.worksheets;
      sheets.load("items/name");
      await context.sync();
      return sheets.items.map((s: any) => s.name);
    });

    const inputSheets = allSheets.filter(n =>
      !n.startsWith("Sample_") &&
      !n.endsWith("_Result") &&
      n !== "00_Recap" &&
      n !== "ZZ_Memo"
    );

    const detected: { [key: string]: string | null } = { gl: null, tb: null, chart: null, tb_n1: null };
    for (const name of inputSheets) {
      const role = guessRole(name);
      if (role && !detected[role]) {
        detected[role] = name;
      }
    }

    const roles: ("gl" | "tb" | "chart" | "tb_n1")[] = ["gl", "tb", "chart", "tb_n1"];
    for (const role of roles) {
      const select = document.getElementById(`map-${role}`) as HTMLSelectElement;
      const status = document.getElementById(`status-${role}`);

      const currentValue = state.sheets[role] || detected[role];
      select.innerHTML = "";

      if (role !== "gl") {
        const noneOpt = document.createElement("option");
        noneOpt.value = "";
        noneOpt.textContent = "(Optionnel)";
        select.appendChild(noneOpt);
      }

      for (const sheetName of inputSheets) {
        const opt = document.createElement("option");
        opt.value = sheetName;
        opt.textContent = sheetName;
        if (sheetName === currentValue) opt.selected = true;
        select.appendChild(opt);
      }

      const finalValue = currentValue || "";
      state.sheets[role] = finalValue || null;

      if (status) {
        if (finalValue) {
          status.textContent = "detecte";
          status.className = "field-status found";
        } else if (role === "gl") {
          status.textContent = "obligatoire";
          status.className = "field-status error";
        } else {
          status.textContent = "absent";
          status.className = "field-status missing";
        }
      }

      select.onchange = () => {
        state.sheets[role] = select.value || null;
        if (status) {
          if (select.value) {
            status.textContent = "OK";
            status.className = "field-status found";
          } else {
            status.textContent = role === "gl" ? "obligatoire" : "absent";
            status.className = role === "gl" ? "field-status error" : "field-status missing";
          }
        }
        refreshStepAvailability();
      };
    }
    // Réinitialiser les mappings (les feuilles ont peut-être changé)
    state.mappings = { gl: {}, tb: {}, tb_n1: {}, chart: {} };
    state.mappingsValidated = { gl: false, tb: false, tb_n1: false, chart: false };

    // Précharger les mappings pour les 4 sheets détectées
    // Permet à state.mappings d'être rempli sans devoir ouvrir manuellement chaque accordion
    const sheetTypesToPreload: ("gl" | "tb" | "tb_n1" | "chart")[] = ["gl", "tb", "tb_n1", "chart"];
    for (const st of sheetTypesToPreload) {
      if (state.sheets[st]) {
        await preloadMappingForSheet(st);
      }
    }

    refreshAllMappingDots();
    // Fermer tous les accordéons ouverts
    document.querySelectorAll<HTMLElement>(".mapping-accordion").forEach(a => {
      a.classList.remove("expanded");
      const id = (a.querySelector(".mapping-header") as HTMLElement).dataset.target!;
      const body = document.getElementById(`map-body-${id}`);
      if (body) body.style.display = "none";
    });
    refreshStepAvailability();
    refreshSetupAccordionSummaries();
  } catch (err: any) {
    setSetupStatus(`Erreur scan : ${err.message}`, "error");
  } finally {
    btn.disabled = false;
  }
}

async function validateSetup() {
  if (!state.sheets.gl) {
    setSetupStatus("Le Grand Livre (GL) est obligatoire pour continuer.", "error");
    return;
  }

  // Si pas de Chart sélectionné mais TB mappée → auto-générer le Chart BEGAAP
  if (!state.sheets.chart && state.sheets.tb && state.mappingsValidated.tb) {
    setSetupStatus("Génération automatique du plan comptable BEGAAP...", "info");
    try {
      await autoGenerateChartBegaap();
    } catch (err: any) {
      setSetupStatus(`Erreur auto-génération du Chart : ${err.message}`, "error");
      return;
    }
  }

  // Vérifier que les mappings des feuilles présentes sont validés
  const requiredMappings: { type: "gl" | "tb" | "tb_n1" | "chart"; label: string }[] = [
    { type: "gl", label: "Grand Livre" },
  ];
  if (state.sheets.tb) requiredMappings.push({ type: "tb", label: "Trial Balance" });
  if (state.sheets.tb_n1) requiredMappings.push({ type: "tb_n1", label: "Trial Balance N-1" });
  if (state.sheets.chart) requiredMappings.push({ type: "chart", label: "Chart of Accounts" });

  const missing = requiredMappings.filter(m => !state.mappingsValidated[m.type]);
  if (missing.length > 0) {
    const missingLabels = missing.map(m => m.label).join(", ");
    setSetupStatus(`Mapping non validé pour : ${missingLabels}.`, "error");
    return;
  }

  state.validated = true;
  invalidateJournalCache();
  refreshStepAvailability();
  document.querySelector<HTMLElement>('.step-btn[data-screen="setup"]')!.classList.add("completed");

  // Sauvegarde silencieuse en arrière-plan, n'affiche que si erreur
  saveMappingsToWorkbook().catch(err => {
    console.warn("Sauvegarde mappings échouée:", err);
    setSetupStatus(`Sauvegarde échouée : ${err.message}`, "error");
  });

  // Vide le status (pas de message "non erreur")
  setSetupStatus("", "info");
  const statusEl = document.getElementById("setup-status");
  if (statusEl) statusEl.className = "status";
}

function refreshStepAvailability() {
  const steps: { screen: string; required: ("gl" | "tb" | "chart" | "tb_n1")[] }[] = [
    { screen: "recon", required: ["gl", "tb"] },
    { screen: "scoping", required: ["gl", "chart"] },
    { screen: "reversals", required: ["gl"] },
    { screen: "analytical", required: ["tb", "tb_n1", "chart"] },
    { screen: "sampling", required: ["gl"] },
    { screen: "export", required: ["gl"] },
  ];

  for (const step of steps) {
    const btn = document.querySelector<HTMLElement>(`.step-btn[data-screen="${step.screen}"]`);
    if (!btn) continue;
    const allPresent = step.required.every(r => state.sheets[r]);
    const allMapped = step.required.every(r => state.mappingsValidated[r]);
    if (allPresent && allMapped) {
      btn.classList.remove("disabled");
    } else {
      btn.classList.add("disabled");
    }
  }
}

function setSetupStatus(message: string, kind: "info" | "success" | "error") {
  const el = document.getElementById("setup-status")!;
  el.textContent = message;
  el.className = `status show ${kind}`;
}

// ============================================================
// SAMPLING SCREEN (déjà fonctionnel)
// ============================================================
function setStatus(message: string, kind: "info" | "success" | "error") {
  const el = document.getElementById("status")!;
  el.textContent = message;
  el.className = `status show ${kind}`;
}

// ============================================================
// SCOPING SCREEN
// ============================================================
function setScopeStatus(message: string, kind: "info" | "success" | "error") {
  const el = document.getElementById("scope-status")!;
  el.textContent = message;
  el.className = `status show ${kind}`;
}

// Convertit un serial Excel (ex: 45307) en date ISO "2024-01-15"
function excelSerialToISO(serial: number): string {
  // Excel epoch = 1899-12-30 (compense le bug Lotus 1-2-3)
  const epoch = new Date(Date.UTC(1899, 11, 30));
  const date = new Date(epoch.getTime() + serial * 86400000);
  return date.toISOString().split("T")[0];
}

// Lit un onglet de résultats stylé (titre row 1, sous-titre row 2, headers row 4 par défaut)
async function readResultSheetAsObjects(sheetName: string, headerRowIndex: number = 3): Promise<any[]> {
  return await Excel.run(async (context) => {
    const sheet = context.workbook.worksheets.getItem(sheetName);
    const range = sheet.getUsedRange();
    range.load(["values", "rowCount"]);
    await context.sync();

    const values = range.values;
    if (values.length <= headerRowIndex + 1) return [];

    const headers = values[headerRowIndex].map((h: any) => String(h).trim());
    const rows: any[] = [];
    for (let i = headerRowIndex + 1; i < values.length; i++) {
      // Skip les rows totalement vides
      if (values[i].every((v: any) => v === "" || v === null || v === undefined)) continue;
      const row: any = {};
      headers.forEach((h: string, idx: number) => {
        row[h] = values[i][idx];
      });
      rows.push(row);
    }
    return rows;
  });
}

async function readSheetAsObjects(sheetName: string): Promise<any[]> {
  return await Excel.run(async (context) => {
    const sheet = context.workbook.worksheets.getItem(sheetName);
    const range = sheet.getUsedRange();
    range.load(["values", "numberFormat"]);
    await context.sync();

    const values = range.values;
    const formats = range.numberFormat;
    if (values.length < 2) return [];

    const headers = values[0].map((h: any) => String(h).trim());

    // Détecter quelles colonnes sont des dates (basé sur le header ET le format)
    const isDateCol: boolean[] = headers.map((h: string, idx: number) => {
      const headerLooksLikeDate = /date/i.test(h);
      const formatLooksLikeDate = formats[1] && typeof formats[1][idx] === "string"
        && /[ymd]/i.test(formats[1][idx]) && !/[$€]/.test(formats[1][idx]);
      return headerLooksLikeDate || formatLooksLikeDate;
    });

    const rows: any[] = [];
    for (let i = 1; i < values.length; i++) {
      const row: any = {};
      headers.forEach((h: string, idx: number) => {
        let v = values[i][idx];
        // Convertir les serials Excel en dates ISO pour les colonnes dates
        if (isDateCol[idx] && typeof v === "number" && v > 0 && v < 100000) {
          v = excelSerialToISO(v);
        }
        row[h] = v;
      });
      rows.push(row);
    }
    return rows;
  });
}
async function readMappedSheet(sheetType: "gl" | "tb" | "tb_n1" | "chart"): Promise<any[]> {
  const sheetName = state.sheets[sheetType];
  if (!sheetName) {
    throw new Error(`Aucune feuille mappée comme '${sheetType}'. Vérifie le Setup.`);
  }

  if (!state.mappingsValidated[sheetType]) {
    throw new Error(`Mapping de '${sheetType}' non validé. Va dans Setup et valide le mapping.`);
  }

  const mapping = state.mappings[sheetType];
  const rawRows = await readSheetAsObjects(sheetName);
  const mapped = applyMapping(rawRows, mapping, sheetType);

  // Auto-génération du line_id si absent (pour GL uniquement)
  if (sheetType === "gl") {
    const hasId = mapped.length > 0 && mapped[0].id !== undefined && mapped[0].id !== null && mapped[0].id !== "";
    if (!hasId) {
      mapped.forEach((row, idx) => {
        row.id = idx + 1;
      });
    }
  }

  return mapped;
}
// ============================================================
// MAPPING PERSISTENCE (Excel CustomXmlParts)
// ============================================================

const MAPPING_NAMESPACE = "https://audit-panel.app/mappings/v1";

async function saveMappingsToWorkbook(): Promise<void> {
  const mappingData = {
    mappings: state.mappings,
    validated: state.mappingsValidated,
    sheets: state.sheets,
    materiality: state.materiality,
    pmPct: state.pmPct,
    cttPct: state.cttPct,
    savedAt: new Date().toISOString(),
  };

  const xml = `<?xml version="1.0" encoding="UTF-8"?>
<auditPanelData xmlns="${MAPPING_NAMESPACE}">
  <data><![CDATA[${JSON.stringify(mappingData)}]]></data>
</auditPanelData>`;

  await Excel.run(async (context) => {
    const customXmlParts = context.workbook.customXmlParts;
    const existingParts = customXmlParts.getByNamespace(MAPPING_NAMESPACE);
    existingParts.load("items/id");
    await context.sync();

    for (const part of existingParts.items) {
      part.delete();
    }
    await context.sync();

    customXmlParts.add(xml);
    await context.sync();
  });
}

async function loadMappingsFromWorkbook(): Promise<boolean> {
  try {
    const xml = await Excel.run(async (context) => {
      const customXmlParts = context.workbook.customXmlParts;
      const parts = customXmlParts.getByNamespace(MAPPING_NAMESPACE);
      parts.load("items/id");
      await context.sync();

      if (parts.items.length === 0) return null;

      const part = parts.items[0];
      const xmlPromise = part.getXml();
      await context.sync();
      return xmlPromise.value;
    });

    if (!xml) return false;

    const match = xml.match(/<!\[CDATA\[(.*?)\]\]>/s);
    if (!match) return false;

    const data = JSON.parse(match[1]);

    if (data.mappings) state.mappings = data.mappings;
    if (data.validated) state.mappingsValidated = data.validated;
    if (data.sheets) state.sheets = data.sheets;
    if (data.materiality) state.materiality = data.materiality;
    if (data.pmPct !== undefined) state.pmPct = data.pmPct;
    if (data.cttPct !== undefined) state.cttPct = data.cttPct;

    // Vérifier que les feuilles enregistrées existent toujours dans le workbook actuel
    await Excel.run(async (context) => {
      const sheets = context.workbook.worksheets;
      sheets.load("items/name");
      await context.sync();
      const existingNames = new Set(sheets.items.map(s => s.name));

      // Pour chaque rôle, si la feuille enregistrée n'existe pas, on nettoie
      for (const role of ["gl", "tb", "tb_n1", "chart"] as const) {
        const sheetName = state.sheets[role];
        if (sheetName && !existingNames.has(sheetName)) {
          state.sheets[role] = null;
          state.mappings[role] = {};
          state.mappingsValidated[role] = false;
        }
      }
    });

    return true;
  } catch (err) {
    console.warn("Erreur lors du chargement des mappings depuis le workbook:", err);
    return false;
  }
}

function bindScopingControls() {
  // Segmented control granularité
  const segContainer = document.getElementById("scope-granularity")!;
  segContainer.querySelectorAll<HTMLButtonElement>(".segment").forEach(seg => {
    seg.onclick = () => {
      segContainer.querySelectorAll(".segment").forEach(s => s.classList.remove("active"));
      seg.classList.add("active");
    };
  });

  // Threshold dropdown + value
  const typeSelect = document.getElementById("scope-threshold-type") as HTMLSelectElement;
  const valueInput = document.getElementById("scope-threshold-value") as HTMLInputElement;

  function syncThreshold() {
    const type = typeSelect.value;
    if (type === "pm") {
      valueInput.value = formatNumberInput(getPM());
      valueInput.readOnly = true;
    } else if (type === "materiality") {
      valueInput.value = formatNumberInput(state.materiality);
      valueInput.readOnly = true;
    } else {
      valueInput.readOnly = false;
      if (!valueInput.value || valueInput.value === "0") {
        valueInput.value = formatNumberInput(getPM());
      }
      valueInput.focus();
    }
  }

  typeSelect.onchange = syncThreshold;
  valueInput.onblur = () => {
    if (!valueInput.readOnly) {
      valueInput.value = formatNumberInput(parseNumberInput(valueInput.value));
    }
  };

  syncThreshold();
}

function renderScopingReview() {
  const block = document.getElementById("scope-review-block")!;
  const list = document.getElementById("scope-review-list")!;
  const toggleBtn = document.getElementById("scope-toggle-oos")!;

  if (!scopingState.loaded || scopingState.rows.length === 0) {
    block.style.display = "none";
    return;
  }

  block.style.display = "block";

  // Trier par valeur décroissante (in-scope en premier groupe, oos après)
  const sortedRows = [...scopingState.rows].sort((a, b) => {
    const va = parseFloat(a["abs_value"] ?? a["value"] ?? "0") || 0;
    const vb = parseFloat(b["abs_value"] ?? b["value"] ?? "0") || 0;
    return vb - va;
  });

  // Filtrer selon le toggle
  const inScopeRows = sortedRows.filter(r => {
    const name = String(r["financial_line"] ?? r["label"] ?? "");
    const dec = scopingState.decisions.get(name);
    return dec && dec.autoInScope;
  });
  const oosRows = sortedRows.filter(r => {
    const name = String(r["financial_line"] ?? r["label"] ?? "");
    const dec = scopingState.decisions.get(name);
    return dec && !dec.autoInScope;
  });

  const visibleRows = scopingState.showOutOfScope ? sortedRows : inScopeRows;

  list.innerHTML = visibleRows.map(row => {
    const name = String(row["financial_line"] ?? row["label"] ?? "");
    const dec = scopingState.decisions.get(name)!;
    const amount = parseFloat(row["abs_value"] ?? row["value"] ?? "0") || 0;
    const isOverride = dec.autoInScope !== dec.userInScope;
    const isOos = !dec.autoInScope;

    let badge = "";
    if (isOverride) badge = `<span class="badge-override">OVERRIDE</span>`;
    else if (isOos) badge = `<span class="badge-oos">OOS</span>`;

    return `
      <label class="section-row ${dec.userInScope ? "" : "unchecked"}" data-name="${name}">
        <input type="checkbox" ${dec.userInScope ? "checked" : ""}>
        <span class="section-name">${formatLabel(name)}${badge}</span>
        <span class="section-amount">${formatEur(amount)}</span>
      </label>
    `;
  }).join("");

  list.querySelectorAll<HTMLInputElement>('input[type="checkbox"]').forEach(cb => {
    cb.onchange = () => {
      const row = cb.closest(".section-row") as HTMLElement;
      const name = row.dataset.name!;
      const dec = scopingState.decisions.get(name)!;
      dec.userInScope = cb.checked;
      renderScopingReview(); // re-render pour mettre à jour le badge OVERRIDE
    };
  });

  // Mise à jour du libellé du bouton toggle
  toggleBtn.textContent = scopingState.showOutOfScope
    ? `− Masquer les sections hors scope (${oosRows.length})`
    : `+ Voir aussi les sections hors scope (${oosRows.length})`;
}

function bindScopingReviewControls() {
  const toggleBtn = document.getElementById("scope-toggle-oos");
  const applyBtn = document.getElementById("scope-apply-btn");

  if (toggleBtn) {
    toggleBtn.onclick = () => {
      scopingState.showOutOfScope = !scopingState.showOutOfScope;
      renderScopingReview();
    };
  }

  if (applyBtn) {
    applyBtn.onclick = applyScopingOverrides;
  }
}

async function applyScopingOverrides() {
  const statusEl = document.getElementById("scope-override-status")!;
  const setOvStatus = (msg: string, kind: "info" | "success" | "error") => {
    statusEl.textContent = msg;
    statusEl.className = `status show ${kind}`;
  };

  const overrideCount = Array.from(scopingState.decisions.values())
    .filter(d => d.autoInScope !== d.userInScope).length;

  if (overrideCount === 0) {
    setOvStatus("Aucun override à appliquer.", "info");
    return;
  }

  setOvStatus(`Application de ${overrideCount} override(s)...`, "info");

  try {
    await Excel.run(async (context) => {
      const sheets = context.workbook.worksheets;
      sheets.load("items/name");
      await context.sync();

      const existing = sheets.items.find((s: any) => s.name === "Scoping_Result");
      if (!existing) {
        throw new Error("Scoping_Result introuvable. Re-lance le scoping d'abord.");
      }
      existing.delete();
      await context.sync();

      const sheet = sheets.add("Scoping_Result");
      sheet.activate();

      // Reconstruire les rows avec les décisions utilisateur + decision_source
      const updatedRows = scopingState.rows.map(row => {
        const name = String(row["financial_line"] ?? row["label"] ?? "");
        const dec = scopingState.decisions.get(name);
        const newRow = { ...row };
        if (dec) {
          newRow["in_scope"] = dec.userInScope;
          if (dec.autoInScope === dec.userInScope) {
            newRow["decision_source"] = "auto";
          } else if (dec.userInScope) {
            newRow["decision_source"] = "qualitative_in";
          } else {
            newRow["decision_source"] = "qualitative_out";
          }
        }
        return newRow;
      });

      if (updatedRows.length === 0) {
        sheet.getRange("A1").values = [["Aucun résultat"]];
        await context.sync();
        return;
      }

      // ============ HEADER (titre + sous-titre) ============
      const titleRange = sheet.getRange("A1");
      titleRange.values = [["Scoping"]];
      titleRange.format.font.name = "Aptos";
      titleRange.format.font.bold = true;
      titleRange.format.font.size = 14;
      titleRange.format.font.color = "#1E40AF";

      const inScopeCount = updatedRows.filter(r => r.in_scope === true).length;
      const overrideCount = updatedRows.filter(r => r.decision_source && r.decision_source !== "auto").length;
      const subtitleRange = sheet.getRange("A2");
      const subtitleText = `${updatedRows.length} financial lines · ${inScopeCount} in scope` + (overrideCount > 0 ? ` · ${overrideCount} override(s)` : "");
      subtitleRange.values = [[subtitleText]];
      subtitleRange.format.font.name = "Aptos";
      subtitleRange.format.font.italic = true;
      subtitleRange.format.font.size = 11;
      subtitleRange.format.font.color = "#6B7280";

      // ============ HEADERS DU TABLEAU ============
      const HEADERS = ["Financial Line", "Type", "Solde / Mvt", "À tester", "Source"];
      const HEADER_ROW = 3;
      const headerRange = sheet.getRangeByIndexes(HEADER_ROW, 0, 1, HEADERS.length);
      headerRange.values = [HEADERS];
      headerRange.format.font.name = "Aptos";
      headerRange.format.font.bold = true;
      headerRange.format.font.size = 11;
      headerRange.format.font.color = "white";
      headerRange.format.fill.color = "#1E40AF";
      headerRange.format.horizontalAlignment = Excel.HorizontalAlignment.center;
      headerRange.format.verticalAlignment = Excel.VerticalAlignment.center;
      headerRange.format.rowHeight = 24;

      // ============ TRI : BS d'abord, puis PL, in_scope d'abord ============
      const sortedRows = [...updatedRows].sort((a, b) => {
        const sectionA = a.statement_type === "BS" ? 0 : 1;
        const sectionB = b.statement_type === "BS" ? 0 : 1;
        if (sectionA !== sectionB) return sectionA - sectionB;
        // Dans la même section : in_scope d'abord, puis par valeur décroissante
        if (a.in_scope !== b.in_scope) return a.in_scope ? -1 : 1;
        return Math.abs(b.value || 0) - Math.abs(a.value || 0);
      });

      // ============ DATA ============
      const dataValues = sortedRows.map((row: any) => [
        row.financial_line,
        row.statement_type,
        row.value,
        row.in_scope ? "Oui" : "Non",
        row.decision_source === "qualitative_in" ? "Override (in)" :
          row.decision_source === "qualitative_out" ? "Override (out)" :
          "Auto",
      ]);
      const dataStartRow = HEADER_ROW + 1;
      const dataRange = sheet.getRangeByIndexes(dataStartRow, 0, dataValues.length, HEADERS.length);
      dataRange.values = dataValues;
      dataRange.format.font.name = "Aptos";
      dataRange.format.font.size = 10;

      // ============ FORMAT NOMBRES ============
      const numFormat = '#,##0.00;(#,##0.00)';
      const valueRange = sheet.getRangeByIndexes(dataStartRow, 2, dataValues.length, 1);
      valueRange.numberFormat = Array(dataValues.length).fill([numFormat]);

      // ============ COLORATION SELON in_scope ET decision_source ============
      for (let i = 0; i < sortedRows.length; i++) {
        const isIn = sortedRows[i].in_scope === true;
        const src = sortedRows[i].decision_source;
        const inScopeCell = sheet.getRangeByIndexes(dataStartRow + i, 3, 1, 1); // colonne "À tester"
        const sourceCell = sheet.getRangeByIndexes(dataStartRow + i, 4, 1, 1); // colonne "Source"

        if (isIn) {
          inScopeCell.format.fill.color = "#DCFCE7";
          inScopeCell.format.font.color = "#15803D";
          inScopeCell.format.font.bold = true;
        } else {
          inScopeCell.format.fill.color = "#F5F5F4";
          inScopeCell.format.font.color = "#71717A";
        }

        if (src && src !== "auto") {
          sourceCell.format.fill.color = "#FEF3C7";
          sourceCell.format.font.color = "#B45309";
          sourceCell.format.font.bold = true;
        }
      }

      // ============ LARGEUR DES COLONNES ============
      sheet.getRange("A:A").format.columnWidth = 220; // Financial Line
      sheet.getRange("B:B").format.columnWidth = 50;  // Type
      sheet.getRange("C:C").format.columnWidth = 130; // Solde / Mvt
      sheet.getRange("D:D").format.columnWidth = 80;  // À tester
      sheet.getRange("E:E").format.columnWidth = 130; // Source

      // ============ FOOTER ============
      const footerRow = dataStartRow + dataValues.length + 2;
      sheet.getRangeByIndexes(footerRow, 0, 1, 1).values = [[`Généré par Audit Panel · ${new Date().toLocaleDateString("fr-BE", { day: "2-digit", month: "2-digit", year: "numeric" })}`]];
      const footerRange = sheet.getRangeByIndexes(footerRow, 0, 1, HEADERS.length);
      footerRange.merge();
      footerRange.format.font.name = "Aptos";
      footerRange.format.font.italic = true;
      footerRange.format.font.size = 9;
      footerRange.format.font.color = "#9CA3AF";

      // ============ CACHER GRIDLINES + GRISER L'EXTÉRIEUR ============
      sheet.gridlines = false;

      const outerGrey = "#E5E7EB";
      const allZone = sheet.getRangeByIndexes(0, 0, 500, 50);
      allZone.format.fill.color = outerGrey;

      const tableArea = sheet.getRangeByIndexes(0, 0, footerRow + 1, HEADERS.length);
      tableArea.format.fill.color = "white";

      // Réappliquer le header bleu
      headerRange.format.fill.color = "#1E40AF";

      // Réappliquer les couleurs in_scope et override
      for (let i = 0; i < sortedRows.length; i++) {
        const isIn = sortedRows[i].in_scope === true;
        const src = sortedRows[i].decision_source;
        const inScopeCell = sheet.getRangeByIndexes(dataStartRow + i, 3, 1, 1);
        const sourceCell = sheet.getRangeByIndexes(dataStartRow + i, 4, 1, 1);

        if (isIn) {
          inScopeCell.format.fill.color = "#DCFCE7";
        } else {
          inScopeCell.format.fill.color = "#F5F5F4";
        }
        if (src && src !== "auto") {
          sourceCell.format.fill.color = "#FEF3C7";
        }
      }

      // ============ BORDURE EXTERNE DU TABLEAU ============
      const tableBorderRange = sheet.getRangeByIndexes(HEADER_ROW, 0, dataValues.length + 1, HEADERS.length);
      const borders = tableBorderRange.format.borders;
      const borderColor = "#1E40AF";

      const edgeTop = borders.getItem("EdgeTop");
      edgeTop.style = Excel.BorderLineStyle.continuous;
      edgeTop.color = borderColor;
      edgeTop.weight = Excel.BorderWeight.medium;

      const edgeBottom = borders.getItem("EdgeBottom");
      edgeBottom.style = Excel.BorderLineStyle.continuous;
      edgeBottom.color = borderColor;
      edgeBottom.weight = Excel.BorderWeight.medium;

      const edgeLeft = borders.getItem("EdgeLeft");
      edgeLeft.style = Excel.BorderLineStyle.continuous;
      edgeLeft.color = borderColor;
      edgeLeft.weight = Excel.BorderWeight.medium;

      const edgeRight = borders.getItem("EdgeRight");
      edgeRight.style = Excel.BorderLineStyle.continuous;
      edgeRight.color = borderColor;
      edgeRight.weight = Excel.BorderWeight.medium;

      await context.sync();
    });

    // Mettre à jour scopingState.rows avec les nouvelles décisions
    for (const row of scopingState.rows) {
      const name = String(row["financial_line"] ?? row["label"] ?? "");
      const dec = scopingState.decisions.get(name);
      if (dec) {
        row["in_scope"] = dec.userInScope;
        dec.autoInScope = dec.userInScope; // l'override devient le nouveau "auto" pour ce session
      }
    }

    setOvStatus(`✓ ${overrideCount} override(s) appliqué(s) à Scoping_Result`, "success");
    renderScopingReview();
  } catch (err: any) {
    console.error(err);
    setOvStatus(`Erreur : ${err.message}`, "error");
  }
}

async function runScope() {
  const btn = document.getElementById("scope-btn") as HTMLButtonElement;
  const summaryDiv = document.getElementById("scope-summary")!;

  const glSheet = state.sheets.gl!;
  const chartSheet = state.sheets.chart!;
  const granularity = document.querySelector<HTMLElement>("#scope-granularity .segment.active")!.dataset.value!;
  const thresholdType = (document.getElementById("scope-threshold-type") as HTMLSelectElement).value;
  const openingJournals = getSelectedJournals("recon-journals");
  let threshold: number;
  let thresholdLabel: string;
  if (thresholdType === "pm") {
    threshold = getPM();
    thresholdLabel = "PM";
  } else if (thresholdType === "materiality") {
    threshold = state.materiality;
    thresholdLabel = "Materiality";
  } else {
    threshold = parseNumberInput((document.getElementById("scope-threshold-value") as HTMLInputElement).value);
    thresholdLabel = "Custom";
  }

  if (threshold <= 0) {
    setScopeStatus("Seuil invalide (revoir Setup)", "error");
    return;
  }

  btn.disabled = true;
  summaryDiv.style.display = "none";
  setScopeStatus(`Lecture des feuilles ${glSheet} et ${chartSheet}...`, "info");

  try {
    const [glLines, chartRows] = await Promise.all([
      readMappedSheet("gl"),
      readMappedSheet("chart"),
    ]);

    if (glLines.length === 0) {
      throw new Error(`Feuille '${glSheet}' vide ou introuvable`);
    }
    if (chartRows.length === 0) {
      throw new Error(`Feuille '${chartSheet}' vide ou introuvable`);
    }

    setScopeStatus(`${glLines.length} lignes GL, ${chartRows.length} comptes chart. Calcul...`, "info");

    const response = await fetch(`${BACKEND_URL}/scope`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        lines: glLines,
        chart: chartRows,
        threshold: threshold,
        granularity: granularity,
        threshold_label: thresholdLabel,
        opening_journals: openingJournals,
      }),
    });

    if (!response.ok) {
      const err = await response.text();
      throw new Error(`Backend ${response.status}: ${err}`);
    }

    const result = await response.json();
    setScopeStatus(`Scoping reçu : ${result.summary.n_lines_in_scope}/${result.summary.n_lines_total} in scope. Écriture...`, "info");

    await Excel.run(async (context) => {
      const sheets = context.workbook.worksheets;
      sheets.load("items/name");
      await context.sync();
      const existing = sheets.items.find((s: any) => s.name === "Scoping_Result");
      if (existing) {
        existing.delete();
        await context.sync();
      }
      const sheet = sheets.add("Scoping_Result");
      sheet.activate();

      const rows = result.rows;
      if (rows.length === 0) {
        sheet.getRange("A1").values = [["Aucun résultat"]];
        await context.sync();
        return;
      }

      // ============ HEADER (titre + sous-titre) ============
      const titleRange = sheet.getRange("A1");
      titleRange.values = [["Scoping"]];
      titleRange.format.font.name = "Aptos";
      titleRange.format.font.bold = true;
      titleRange.format.font.size = 14;
      titleRange.format.font.color = "#1E40AF";

      const inScopeCount = result.summary.n_lines_in_scope;
      const subtitleRange = sheet.getRange("A2");
      const subtitleText = `${rows.length} financial lines · ${inScopeCount} in scope · Seuil ${result.summary.threshold_label} €${result.summary.threshold.toLocaleString()} · Granularité ${result.summary.granularity}`;
      subtitleRange.values = [[subtitleText]];
      subtitleRange.format.font.name = "Aptos";
      subtitleRange.format.font.italic = true;
      subtitleRange.format.font.size = 11;
      subtitleRange.format.font.color = "#6B7280";

      // ============ HEADERS DU TABLEAU ============
      const HEADERS = ["Financial Line", "Type", "Solde / Mvt", "À tester"];
      const HEADER_ROW = 3;
      const headerRange = sheet.getRangeByIndexes(HEADER_ROW, 0, 1, HEADERS.length);
      headerRange.values = [HEADERS];
      headerRange.format.font.name = "Aptos";
      headerRange.format.font.bold = true;
      headerRange.format.font.size = 11;
      headerRange.format.font.color = "white";
      headerRange.format.fill.color = "#1E40AF";
      headerRange.format.horizontalAlignment = Excel.HorizontalAlignment.center;
      headerRange.format.verticalAlignment = Excel.VerticalAlignment.center;
      headerRange.format.rowHeight = 24;

      // ============ TRI : BS d'abord, puis PL, in_scope d'abord ============
      const sortedRows = [...rows].sort((a: any, b: any) => {
        const sectionA = a.statement_type === "BS" ? 0 : 1;
        const sectionB = b.statement_type === "BS" ? 0 : 1;
        if (sectionA !== sectionB) return sectionA - sectionB;
        if (a.in_scope !== b.in_scope) return a.in_scope ? -1 : 1;
        return Math.abs(b.value || 0) - Math.abs(a.value || 0);
      });

      // ============ DATA ============
      const dataValues = sortedRows.map((row: any) => [
        row.financial_line,
        row.statement_type,
        row.value,
        row.in_scope ? "Oui" : "Non",
      ]);
      const dataStartRow = HEADER_ROW + 1;
      const dataRange = sheet.getRangeByIndexes(dataStartRow, 0, dataValues.length, HEADERS.length);
      dataRange.values = dataValues;
      dataRange.format.font.name = "Aptos";
      dataRange.format.font.size = 10;

      // ============ FORMAT NOMBRES ============
      const numFormat = '#,##0.00;(#,##0.00)';
      const valueRange = sheet.getRangeByIndexes(dataStartRow, 2, dataValues.length, 1);
      valueRange.numberFormat = Array(dataValues.length).fill([numFormat]);

      // ============ COLORATION SELON in_scope ============
      for (let i = 0; i < sortedRows.length; i++) {
        const isIn = sortedRows[i].in_scope === true;
        const inScopeCell = sheet.getRangeByIndexes(dataStartRow + i, 3, 1, 1);

        if (isIn) {
          inScopeCell.format.fill.color = "#DCFCE7";
          inScopeCell.format.font.color = "#15803D";
          inScopeCell.format.font.bold = true;
        } else {
          inScopeCell.format.fill.color = "#F5F5F4";
          inScopeCell.format.font.color = "#71717A";
        }
      }

      // ============ LARGEUR DES COLONNES ============
      sheet.getRange("A:A").format.columnWidth = 220; // Financial Line
      sheet.getRange("B:B").format.columnWidth = 50;  // Type
      sheet.getRange("C:C").format.columnWidth = 130; // Solde / Mvt
      sheet.getRange("D:D").format.columnWidth = 80;  // À tester

      // ============ FOOTER ============
      const footerRow = dataStartRow + dataValues.length + 2;
      sheet.getRangeByIndexes(footerRow, 0, 1, 1).values = [[`Généré par Audit Panel · ${new Date().toLocaleDateString("fr-BE", { day: "2-digit", month: "2-digit", year: "numeric" })}`]];
      const footerRange = sheet.getRangeByIndexes(footerRow, 0, 1, HEADERS.length);
      footerRange.merge();
      footerRange.format.font.name = "Aptos";
      footerRange.format.font.italic = true;
      footerRange.format.font.size = 9;
      footerRange.format.font.color = "#9CA3AF";

      // ============ CACHER GRIDLINES + GRISER L'EXTÉRIEUR ============
      sheet.gridlines = false;

      const outerGrey = "#E5E7EB";
      const allZone = sheet.getRangeByIndexes(0, 0, 500, 50);
      allZone.format.fill.color = outerGrey;

      const tableArea = sheet.getRangeByIndexes(0, 0, footerRow + 1, HEADERS.length);
      tableArea.format.fill.color = "white";

      // Réappliquer le header bleu
      headerRange.format.fill.color = "#1E40AF";

      // Réappliquer les couleurs in_scope
      for (let i = 0; i < sortedRows.length; i++) {
        const isIn = sortedRows[i].in_scope === true;
        const inScopeCell = sheet.getRangeByIndexes(dataStartRow + i, 3, 1, 1);
        if (isIn) {
          inScopeCell.format.fill.color = "#DCFCE7";
        } else {
          inScopeCell.format.fill.color = "#F5F5F4";
        }
      }

      // ============ BORDURE EXTERNE DU TABLEAU ============
      const tableBorderRange = sheet.getRangeByIndexes(HEADER_ROW, 0, dataValues.length + 1, HEADERS.length);
      const borders = tableBorderRange.format.borders;
      const borderColor = "#1E40AF";

      const edgeTop = borders.getItem("EdgeTop");
      edgeTop.style = Excel.BorderLineStyle.continuous;
      edgeTop.color = borderColor;
      edgeTop.weight = Excel.BorderWeight.medium;

      const edgeBottom = borders.getItem("EdgeBottom");
      edgeBottom.style = Excel.BorderLineStyle.continuous;
      edgeBottom.color = borderColor;
      edgeBottom.weight = Excel.BorderWeight.medium;

      const edgeLeft = borders.getItem("EdgeLeft");
      edgeLeft.style = Excel.BorderLineStyle.continuous;
      edgeLeft.color = borderColor;
      edgeLeft.weight = Excel.BorderWeight.medium;

      const edgeRight = borders.getItem("EdgeRight");
      edgeRight.style = Excel.BorderLineStyle.continuous;
      edgeRight.color = borderColor;
      edgeRight.weight = Excel.BorderWeight.medium;

      await context.sync();
    });

    document.getElementById("scope-in")!.textContent = String(result.summary.n_lines_in_scope);
    document.getElementById("scope-out")!.textContent = String(result.summary.n_lines_total - result.summary.n_lines_in_scope);
    document.getElementById("scope-total")!.textContent = String(result.summary.n_lines_total);
    document.getElementById("scope-thr")!.textContent = formatEur(result.summary.threshold);
    summaryDiv.style.display = "block";

    setScopeStatus(`✓ ${result.summary.n_lines_in_scope} financial lines in scope`, "success");
    document.querySelector<HTMLElement>('.step-btn[data-screen="scoping"]')!.classList.add("completed");
  // Initialiser l'état de révision
    scopingState.rows = result.rows;
    scopingState.decisions.clear();
    for (const row of result.rows) {
      const name = String(row["financial_line"] ?? row["label"] ?? "");
      const inScope = row["in_scope"] === true || row["in_scope"] === "TRUE";
      scopingState.decisions.set(name, { autoInScope: inScope, userInScope: inScope });
    }
    scopingState.showOutOfScope = false;
    scopingState.loaded = true;
    renderScopingReview();
  } catch (err: any) {
    console.error(err);
    setScopeStatus(`Erreur : ${err.message}`, "error");
  } finally {
    btn.disabled = false;
  }
}

// ============================================================
// TB RECON SCREEN
// ============================================================
function setReconStatus(message: string, kind: "info" | "success" | "error") {
  const el = document.getElementById("recon-status")!;
  el.textContent = message;
  el.className = `status show ${kind}`;
}

async function runRecon() {
  const btn = document.getElementById("recon-btn") as HTMLButtonElement;
  const summaryDiv = document.getElementById("recon-summary")!;
  const glSheet = state.sheets.gl!;
  const tbSheet = state.sheets.tb!;
  const openingJournals = getSelectedJournals("recon-journals");
  const tolerance = parseFloat((document.getElementById("recon-tolerance") as HTMLInputElement).value) || 0.01;

  btn.disabled = true;
  summaryDiv.style.display = "none";
  setReconStatus(`Lecture des feuilles ${glSheet} et ${tbSheet}...`, "info");

  try {
    const [glLines, tbRows] = await Promise.all([
      readMappedSheet("gl"),
      readMappedSheet("tb"),
    ]);
    if (glLines.length === 0) throw new Error(`Feuille '${glSheet}' vide ou introuvable`);
    if (tbRows.length === 0) throw new Error(`Feuille '${tbSheet}' vide ou introuvable`);

    // Lire aussi TB_N1 et Chart si disponibles (pour la logique BS/PL différenciée)
    let tbN1Rows: any[] = [];
    let chartRows: any[] = [];
    if (state.sheets.tb_n1 && state.mappingsValidated.tb_n1) {
      tbN1Rows = await readMappedSheet("tb_n1");
    }
    if (state.sheets.chart && state.mappingsValidated.chart) {
      chartRows = await readMappedSheet("chart");
    }

    setReconStatus(`${glLines.length} lignes GL, ${tbRows.length} comptes TB. Calcul...`, "info");
    const response = await fetch(`${BACKEND_URL}/recon`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        lines: glLines,
        tb: tbRows,
        tb_n1: tbN1Rows,
        chart: chartRows,
        opening_journals: openingJournals,
        tolerance: tolerance,
      }),
    });

    if (!response.ok) {
      const err = await response.text();
      throw new Error(`Backend ${response.status}: ${err}`);
    }

    const result = await response.json();
    const s = result.summary;
    const missingTotal = (s.missing_in_gl_count || 0) + (s.missing_in_tb_count || 0);

    setReconStatus(`Recon reçu : ${s.ok_count} OK, ${s.ko_count} KO. Écriture...`, "info");

    await Excel.run(async (context) => {
      const sheets = context.workbook.worksheets;
      sheets.load("items/name");
      await context.sync();

      const existing = sheets.items.find((s: any) => s.name === "Recon_Result");
      if (existing) {
        existing.delete();
        await context.sync();
      }

      const sheet = sheets.add("Recon_Result");
      sheet.activate();

      const rows = result.rows;
      if (rows.length === 0) {
        sheet.getRange("A1").values = [["Aucun résultat"]];
        await context.sync();
        return;
      }

      // ============ HEADER (titre + sous-titre) ============
      const titleRange = sheet.getRange("A1");
      titleRange.values = [["TB ↔ GL Reconciliation"]];
      titleRange.format.font.name = "Aptos";
      titleRange.format.font.bold = true;
      titleRange.format.font.size = 14;
      titleRange.format.font.color = "#1E40AF";

      const subtitleRange = sheet.getRange("A2");
      subtitleRange.values = [[
        `${rows.length} comptes · Tolérance €${tolerance} · OK: ${s.ok_count} · Warning: ${s.warning_count} · KO: ${s.ko_count} · Missing: ${missingTotal}`
      ]];
      subtitleRange.format.font.name = "Aptos";
      subtitleRange.format.font.italic = true;
      subtitleRange.format.font.size = 11;
      subtitleRange.format.font.color = "#6B7280";

      // ============ HEADERS DU TABLEAU ============
      const HEADERS = ["Compte", "Libellé", "Type", "Solde N", "Opening", "Mvt attendus", "Mvt GL", "Écart", "Status", "Commentaire"];
      const HEADER_ROW = 3; // ligne 4
      const headerRange = sheet.getRangeByIndexes(HEADER_ROW, 0, 1, HEADERS.length);
      headerRange.values = [HEADERS];
      headerRange.format.font.name = "Aptos";
      headerRange.format.font.bold = true;
      headerRange.format.font.size = 11;
      headerRange.format.font.color = "white";
      headerRange.format.fill.color = "#1E40AF";
      headerRange.format.horizontalAlignment = Excel.HorizontalAlignment.center;
      headerRange.format.verticalAlignment = Excel.VerticalAlignment.center;
      headerRange.format.rowHeight = 24;

      // ============ DATA ============
      const dataValues = rows.map((row: any) => [
        row.compte,
        row.libelle_compte,
        row.statement_type,
        row.solde_n,
        row.ob_used,
        row.expected_movements,
        row.actual_movements,
        row.diff,
        row.status,
        row.comment,
      ]);
      const dataStartRow = HEADER_ROW + 1;
      const dataRange = sheet.getRangeByIndexes(dataStartRow, 0, dataValues.length, HEADERS.length);
      dataRange.values = dataValues;
      dataRange.format.font.name = "Aptos";
      dataRange.format.font.size = 10;

      // ============ FORMAT DES NOMBRES (parenthèses pour négatifs) ============
      const numFormat = '#,##0.00;(#,##0.00)';
      for (const colIdx of [3, 4, 5, 6, 7]) { // Solde N, Opening, Mvt attendus, Mvt GL, Écart
        const colRange = sheet.getRangeByIndexes(dataStartRow, colIdx, dataValues.length, 1);
        colRange.numberFormat = Array(dataValues.length).fill([numFormat]);
      }

      // ============ COLORATION SELON STATUS ============
      for (let i = 0; i < rows.length; i++) {
        const status = String(rows[i].status || "");
        const statusCell = sheet.getRangeByIndexes(dataStartRow + i, 8, 1, 1);
        const rowRange = sheet.getRangeByIndexes(dataStartRow + i, 0, 1, HEADERS.length);

        if (status === "OK" || status === "OK_NO_MOVEMENT") {
          statusCell.format.fill.color = "#DCFCE7";
          statusCell.format.font.color = "#15803D";
          statusCell.format.font.bold = true;
        } else if (status === "WARNING") {
          statusCell.format.fill.color = "#FEF3C7";
          statusCell.format.font.color = "#B45309";
          statusCell.format.font.bold = true;
        } else if (status === "KO" || status === "MISSING_IN_GL" || status === "MISSING_IN_TB") {
          statusCell.format.fill.color = "#FEE2E2";
          statusCell.format.font.color = "#B91C1C";
          statusCell.format.font.bold = true;
          // Toute la ligne en fond rouge pâle pour les problèmes graves
          rowRange.format.fill.color = "#FEF2F2";
        } else if (status === "REQUIRES_OPENING") {
          statusCell.format.fill.color = "#E0E7FF";
          statusCell.format.font.color = "#3730A3";
          statusCell.format.font.bold = true;
        }

        // Mise en gras de l'écart si non zéro
        if (Math.abs(rows[i].diff || 0) > 0.01) {
          const diffCell = sheet.getRangeByIndexes(dataStartRow + i, 7, 1, 1);
          diffCell.format.font.bold = true;
        }
      }

      // ============ LARGEUR DES COLONNES ============
      sheet.getRange("A:A").format.columnWidth = 75;  // Compte
      sheet.getRange("B:B").format.columnWidth = 200; // Libellé
      sheet.getRange("C:C").format.columnWidth = 50;  // Type
      sheet.getRange("D:D").format.columnWidth = 105; // Solde N
      sheet.getRange("E:E").format.columnWidth = 95;  // Opening
      sheet.getRange("F:F").format.columnWidth = 105; // Mvt attendus
      sheet.getRange("G:G").format.columnWidth = 105; // Mvt GL
      sheet.getRange("H:H").format.columnWidth = 95;  // Écart
      sheet.getRange("I:I").format.columnWidth = 130; // Status
      sheet.getRange("J:J").format.columnWidth = 280; // Commentaire

      // ============ FOOTER ============
      const footerRow = dataStartRow + dataValues.length + 2;
      sheet.getRangeByIndexes(footerRow, 0, 1, 1).values = [[`Généré par Audit Panel · ${new Date().toLocaleDateString("fr-BE", { day: "2-digit", month: "2-digit", year: "numeric" })}`]];
      const footerRange = sheet.getRangeByIndexes(footerRow, 0, 1, HEADERS.length);
      footerRange.merge();
      footerRange.format.font.name = "Aptos";
      footerRange.format.font.italic = true;
      footerRange.format.font.size = 9;
      footerRange.format.font.color = "#9CA3AF";

      // ============ CACHER GRIDLINES + GRISER L'EXTÉRIEUR ============
      sheet.gridlines = false;

      const outerGrey = "#E5E7EB";
      const allZone = sheet.getRangeByIndexes(0, 0, 500, 50);
      allZone.format.fill.color = outerGrey;

      // Remettre blanc sur la zone du tableau
      const tableArea = sheet.getRangeByIndexes(0, 0, footerRow + 1, HEADERS.length);
      tableArea.format.fill.color = "white";

      // Réappliquer les couleurs spécifiques (header bleu + status colorés)
      headerRange.format.fill.color = "#1E40AF";
      for (let i = 0; i < rows.length; i++) {
        const status = String(rows[i].status || "");
        const statusCell = sheet.getRangeByIndexes(dataStartRow + i, 8, 1, 1);
        const rowRange = sheet.getRangeByIndexes(dataStartRow + i, 0, 1, HEADERS.length);

        if (status === "OK" || status === "OK_NO_MOVEMENT") {
          statusCell.format.fill.color = "#DCFCE7";
        } else if (status === "WARNING") {
          statusCell.format.fill.color = "#FEF3C7";
        } else if (status === "KO" || status === "MISSING_IN_GL" || status === "MISSING_IN_TB") {
          statusCell.format.fill.color = "#FEE2E2";
          rowRange.format.fill.color = "#FEF2F2";
          statusCell.format.fill.color = "#FEE2E2"; // re-apply après row fill
        } else if (status === "REQUIRES_OPENING") {
          statusCell.format.fill.color = "#E0E7FF";
        }
      }

      // ============ BORDURE EXTERNE DU TABLEAU ============
      const tableBorderRange = sheet.getRangeByIndexes(HEADER_ROW, 0, dataValues.length + 1, HEADERS.length);
      const borders = tableBorderRange.format.borders;
      const borderColor = "#1E40AF";

      const edgeTop = borders.getItem("EdgeTop");
      edgeTop.style = Excel.BorderLineStyle.continuous;
      edgeTop.color = borderColor;
      edgeTop.weight = Excel.BorderWeight.medium;

      const edgeBottom = borders.getItem("EdgeBottom");
      edgeBottom.style = Excel.BorderLineStyle.continuous;
      edgeBottom.color = borderColor;
      edgeBottom.weight = Excel.BorderWeight.medium;

      const edgeLeft = borders.getItem("EdgeLeft");
      edgeLeft.style = Excel.BorderLineStyle.continuous;
      edgeLeft.color = borderColor;
      edgeLeft.weight = Excel.BorderWeight.medium;

      const edgeRight = borders.getItem("EdgeRight");
      edgeRight.style = Excel.BorderLineStyle.continuous;
      edgeRight.color = borderColor;
      edgeRight.weight = Excel.BorderWeight.medium;

      await context.sync();
    });

    document.getElementById("recon-ok")!.textContent = String(s.ok_count);
    document.getElementById("recon-warn")!.textContent = String(s.warning_count);
    document.getElementById("recon-ko")!.textContent = String(s.ko_count);
    document.getElementById("recon-missing")!.textContent = String(missingTotal);
    summaryDiv.style.display = "block";

    if (s.ko_count === 0 && missingTotal === 0) {
      setReconStatus(`✓ TB et GL réconcilient (${s.ok_count} comptes OK)`, "success");
    } else {
      setReconStatus(`⚠ ${s.ko_count} KO + ${missingTotal} missing à investiguer`, "error");
    }
    document.querySelector<HTMLElement>('.step-btn[data-screen="recon"]')!.classList.add("completed");
  } catch (err: any) {
    console.error(err);
    setReconStatus(`Erreur : ${err.message}`, "error");
  } finally {
    btn.disabled = false;
  }
}

// ============================================================
// REVERSALS SCREEN
// ============================================================
function setRevStatus(message: string, kind: "info" | "success" | "error") {
  const el = document.getElementById("rev-status")!;
  el.textContent = message;
  el.className = `status show ${kind}`;
}

function bindReversalsScreen() {
  const winInput = document.getElementById("rev-window-input") as HTMLInputElement;
  const winDisplay = document.getElementById("rev-window-display")!;
  const certInput = document.getElementById("rev-certain-input") as HTMLInputElement;
  const certDisplay = document.getElementById("rev-certain-display")!;
  const probInput = document.getElementById("rev-probable-input") as HTMLInputElement;
  const probDisplay = document.getElementById("rev-probable-display")!;

  winInput.oninput = () => { winDisplay.textContent = winInput.value; };
  certInput.oninput = () => {
    certDisplay.textContent = certInput.value;
    // assurer cohérence : probable < certain
    if (parseFloat(probInput.value) >= parseFloat(certInput.value)) {
      probInput.value = String(parseFloat(certInput.value) - 5);
      probDisplay.textContent = probInput.value;
    }
  };
  probInput.oninput = () => { probDisplay.textContent = probInput.value; };
}

async function runReversals() {
  const btn = document.getElementById("rev-btn") as HTMLButtonElement;
  const summaryDiv = document.getElementById("rev-summary")!;

  const glSheet = state.sheets.gl!;
  const windowDays = parseInt((document.getElementById("rev-window-input") as HTMLInputElement).value);
  const certainTh = parseFloat((document.getElementById("rev-certain-input") as HTMLInputElement).value);
  const probableTh = parseFloat((document.getElementById("rev-probable-input") as HTMLInputElement).value);
  const openingJournals = getSelectedJournals("recon-journals");

  btn.disabled = true;
  summaryDiv.style.display = "none";
  setRevStatus(`Lecture de la feuille ${glSheet}...`, "info");

  try {
    const glLines = await readMappedSheet("gl");

    // Filtrer par sections cochées si Scoping a été chargé
    let linesToAnalyze = glLines;
    if (reversalsState.loaded && reversalsState.sections.length > 0) {
      const accountsSet = new Set<string>();
      for (const sec of reversalsState.sections) {
        if (sec.checked) sec.accounts.forEach(a => accountsSet.add(a));
      }
      if (accountsSet.size === 0) {
        throw new Error("Aucune section sélectionnée. Coche au moins une financial line.");
      }
      linesToAnalyze = glLines.filter((l: any) => accountsSet.has(String(l["compte"])));
      setRevStatus(`${linesToAnalyze.length} lignes (filtre actif sur ${accountsSet.size} comptes). Détection...`, "info");
    } else {
      setRevStatus(`${linesToAnalyze.length} lignes GL. Détection (peut prendre 10-30s)...`, "info");
    }
    if (glLines.length === 0) throw new Error(`Feuille '${glSheet}' vide ou introuvable`);

    setRevStatus(`${glLines.length} lignes GL. Détection (peut prendre 10-30s)...`, "info");

    const response = await fetch(`${BACKEND_URL}/reversals`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        lines: linesToAnalyze,
        window_days: windowDays,
        certain_threshold: certainTh,
        probable_threshold: probableTh,
        opening_journals: openingJournals,
      }),
    });

    if (!response.ok) {
      const err = await response.text();
      throw new Error(`Backend ${response.status}: ${err}`);
    }

    const result = await response.json();
    const s = result.summary;
    setRevStatus(`${s.certain_count} CERTAIN, ${s.probable_count} PROBABLE. Écriture...`, "info");

    await Excel.run(async (context) => {
      const sheets = context.workbook.worksheets;
      sheets.load("items/name");
      await context.sync();

      const existing = sheets.items.find((s: any) => s.name === "Reversals_Result");
      if (existing) {
        existing.delete();
        await context.sync();
      }

      const sheet = sheets.add("Reversals_Result");
      sheet.activate();

      const rows = result.rows;
      if (rows.length === 0) {
        sheet.getRange("A1").values = [["Aucun reversal détecté"]];
        await context.sync();
        return;
      }

      // ============ TRI : par compte croissant, puis CERTAIN avant PROBABLE ============
      const sortedRows = [...rows].sort((a: any, b: any) => {
        const compteA = String(a.compte ?? "");
        const compteB = String(b.compte ?? "");
        if (compteA < compteB) return -1;
        if (compteA > compteB) return 1;
        const bucketOrder: { [k: string]: number } = { "CERTAIN": 0, "PROBABLE": 1 };
        const ba = bucketOrder[a.bucket] ?? 2;
        const bb = bucketOrder[b.bucket] ?? 2;
        if (ba !== bb) return ba - bb;
        return String(a.date_original).localeCompare(String(b.date_original));
      });

      // ============ LOOKUP : index ligne_id -> ligne GL complète ============
      const glById = new Map<string, any>();
      for (const line of glLines) {
        const lid = line["id"] ?? line["ligne_id"];
        if (lid !== undefined && lid !== null) {
          glById.set(String(lid), line);
        }
      }

      // ============ DÉTECTION DU MODE (split vs single) ============
      const firstGl = glLines[0] || {};
      const isModeSplit = firstGl["debit"] !== undefined && firstGl["credit"] !== undefined
                          && !(firstGl["montant"] !== undefined && firstGl["solde"] === undefined);
      const hasJournal = firstGl["journal"] !== undefined;

      // ============ HEADER (titre + sous-titre) ============
      const titleRange = sheet.getRange("A1");
      titleRange.values = [["Reversals Detection"]];
      titleRange.format.font.name = "Aptos";
      titleRange.format.font.bold = true;
      titleRange.format.font.size = 14;
      titleRange.format.font.color = "#1E40AF";

      const subtitleRange = sheet.getRange("A2");
      const subtitleText = `${rows.length} paires détectées · CERTAIN: ${s.certain_count} · PROBABLE: ${s.probable_count} · Fenêtre ${windowDays}j · Seuils ${certainTh}/${probableTh}`;
      subtitleRange.values = [[subtitleText]];
      subtitleRange.format.font.name = "Aptos";
      subtitleRange.format.font.italic = true;
      subtitleRange.format.font.size = 11;
      subtitleRange.format.font.color = "#6B7280";

      // ============ HEADERS DU TABLEAU (avec Decision en dernier) ============
      let HEADERS: string[];
      if (isModeSplit) {
        HEADERS = hasJournal
          ? ["Compte", "Date", "Débit", "Crédit", "Libellé", "Journal", "Line ID", "Δ jours", "Score", "Bucket", "Decision"]
          : ["Compte", "Date", "Débit", "Crédit", "Libellé", "Line ID", "Δ jours", "Score", "Bucket", "Decision"];
      } else {
        HEADERS = hasJournal
          ? ["Compte", "Date", "Montant", "Libellé", "Journal", "Line ID", "Δ jours", "Score", "Bucket", "Decision"]
          : ["Compte", "Date", "Montant", "Libellé", "Line ID", "Δ jours", "Score", "Bucket", "Decision"];
      }
      const HEADER_ROW = 3;
      const headerRange = sheet.getRangeByIndexes(HEADER_ROW, 0, 1, HEADERS.length);
      headerRange.values = [HEADERS];
      headerRange.format.font.name = "Aptos";
      headerRange.format.font.bold = true;
      headerRange.format.font.size = 11;
      headerRange.format.font.color = "white";
      headerRange.format.fill.color = "#1E40AF";
      headerRange.format.horizontalAlignment = Excel.HorizontalAlignment.center;
      headerRange.format.verticalAlignment = Excel.VerticalAlignment.center;
      headerRange.format.rowHeight = 24;

      // ============ DATA : 2 LIGNES PAR PAIRE ============
      const dataValues: any[][] = [];

      for (let pi = 0; pi < sortedRows.length; pi++) {
        const pair = sortedRows[pi];
        const origId = String(pair.original_id);
        const revId = String(pair.reversal_id);
        const origLine = glById.get(origId) || {};
        const revLine = glById.get(revId) || {};

        // Décision par défaut selon le bucket
        const initialDecision = pair.bucket === "CERTAIN" ? "Reversal" : "Pending";

        function buildRow(line: any, isFirstOfPair: boolean): any[] {
          const row: any[] = [];
          row.push(line["compte"] ?? pair.compte);
          row.push(line["date"] ?? "");
          if (isModeSplit) {
            row.push(line["debit"] ?? 0);
            row.push(line["credit"] ?? 0);
          } else {
            row.push(line["montant"] ?? line["solde"] ?? 0);
          }
          row.push(line["libelle"] ?? "");
          if (hasJournal) row.push(line["journal"] ?? "");
          row.push(line["id"] ?? line["ligne_id"] ?? "");
          if (isFirstOfPair) {
            row.push(pair.days_diff);
            row.push((pair.score || 0) / 100);
            row.push(pair.bucket);
            row.push(initialDecision);
          } else {
            row.push("");
            row.push("");
            row.push("");
            row.push("");
          }
          return row;
        }

        dataValues.push(buildRow(origLine, true));
        dataValues.push(buildRow(revLine, false));
      }

      const dataStartRow = HEADER_ROW + 1;
      const dataRange = sheet.getRangeByIndexes(dataStartRow, 0, dataValues.length, HEADERS.length);
      dataRange.values = dataValues;
      dataRange.format.font.name = "Aptos";
      dataRange.format.font.size = 10;

      // ============ FORMAT DES CELLULES ============
      const numFormat = '#,##0.00;(#,##0.00)';
      const dateCol = sheet.getRangeByIndexes(dataStartRow, 1, dataValues.length, 1);
      dateCol.numberFormat = Array(dataValues.length).fill(["dd/mm/yyyy"]);

      if (isModeSplit) {
        for (const colIdx of [2, 3]) {
          const colRange = sheet.getRangeByIndexes(dataStartRow, colIdx, dataValues.length, 1);
          colRange.numberFormat = Array(dataValues.length).fill([numFormat]);
        }
      } else {
        const montantCol = sheet.getRangeByIndexes(dataStartRow, 2, dataValues.length, 1);
        montantCol.numberFormat = Array(dataValues.length).fill([numFormat]);
      }

      // Score : 4e dernière colonne (avant Bucket et Decision)
      const scoreColIdx = HEADERS.length - 3;
      const scoreCol = sheet.getRangeByIndexes(dataStartRow, scoreColIdx, dataValues.length, 1);
      scoreCol.numberFormat = Array(dataValues.length).fill(["0.0%"]);

      // ============ MERGE DES COLONNES "PAIR-LEVEL" (Δ jours, Score, Bucket, Decision) ============
      const pairLevelCols = [
        HEADERS.length - 4, // Δ jours
        HEADERS.length - 3, // Score
        HEADERS.length - 2, // Bucket
        HEADERS.length - 1, // Decision
      ];

      for (let pi = 0; pi < sortedRows.length; pi++) {
        const topRowExcel = dataStartRow + (pi * 2);
        for (const colIdx of pairLevelCols) {
          const mergeRange = sheet.getRangeByIndexes(topRowExcel, colIdx, 2, 1);
          mergeRange.merge();
          mergeRange.format.verticalAlignment = Excel.VerticalAlignment.center;
          mergeRange.format.horizontalAlignment = Excel.HorizontalAlignment.center;
        }
      }

      // ============ COLORATION : Bucket + Decision ============
      const bucketColIdx = HEADERS.length - 2;
      const decisionColIdx = HEADERS.length - 1;
      for (let pi = 0; pi < sortedRows.length; pi++) {
        const topRowExcel = dataStartRow + (pi * 2);
        // Bucket
        const bucketCell = sheet.getRangeByIndexes(topRowExcel, bucketColIdx, 1, 1);
        const bucket = sortedRows[pi].bucket;
        if (bucket === "CERTAIN") {
          bucketCell.format.fill.color = "#FEE2E2";
          bucketCell.format.font.color = "#B91C1C";
          bucketCell.format.font.bold = true;
        } else if (bucket === "PROBABLE") {
          bucketCell.format.fill.color = "#FEF3C7";
          bucketCell.format.font.color = "#B45309";
          bucketCell.format.font.bold = true;
        }
        // Decision (couleur selon valeur initiale)
        const decisionCell = sheet.getRangeByIndexes(topRowExcel, decisionColIdx, 1, 1);
        const initialDecision = bucket === "CERTAIN" ? "Reversal" : "Pending";
        if (initialDecision === "Reversal") {
          decisionCell.format.fill.color = "#D1FAE5"; // vert clair
          decisionCell.format.font.color = "#065F46"; // vert foncé
          decisionCell.format.font.bold = true;
        } else if (initialDecision === "Pending") {
          decisionCell.format.fill.color = "#E0E7FF"; // bleu clair
          decisionCell.format.font.color = "#3730A3"; // bleu foncé
          decisionCell.format.font.bold = true;
        }
      }

      // ============ LISTE DÉROULANTE (DATA VALIDATION) SUR LA COLONNE DECISION ============
      // Note : on l'applique sur les cellules mergées (les 1ères lignes de chaque paire)
      for (let pi = 0; pi < sortedRows.length; pi++) {
        const topRowExcel = dataStartRow + (pi * 2);
        const decisionCell = sheet.getRangeByIndexes(topRowExcel, decisionColIdx, 1, 1);
        decisionCell.dataValidation.rule = {
          list: {
            inCellDropDown: true,
            source: "Reversal,Not reversal,Pending"
          }
        };
        decisionCell.dataValidation.errorAlert = {
          message: "Veuillez choisir : Reversal, Not reversal ou Pending",
          showAlert: true,
          style: Excel.DataValidationAlertStyle.stop,
          title: "Valeur invalide"
        };
      }

      // ============ FORMATAGE CONDITIONNEL SUR LA COLONNE DECISION ============
      // Permet à Excel de re-colorer automatiquement quand l'utilisateur override
      // directement dans la cellule (via la liste déroulante)
      for (let pi = 0; pi < sortedRows.length; pi++) {
        const topRowExcel = dataStartRow + (pi * 2);
        const decisionCell = sheet.getRangeByIndexes(topRowExcel, decisionColIdx, 1, 1);

        // Règle 1 : "Reversal" → vert
        const ruleReversal = decisionCell.conditionalFormats.add(Excel.ConditionalFormatType.cellValue);
        ruleReversal.cellValue!.format.fill.color = "#D1FAE5";
        ruleReversal.cellValue!.format.font.color = "#065F46";
        ruleReversal.cellValue!.format.font.bold = true;
        ruleReversal.cellValue!.rule = {
          formula1: '"Reversal"',
          operator: Excel.ConditionalCellValueOperator.equalTo
        };

        // Règle 2 : "Not reversal" → rouge
        const ruleNot = decisionCell.conditionalFormats.add(Excel.ConditionalFormatType.cellValue);
        ruleNot.cellValue!.format.fill.color = "#FEE2E2";
        ruleNot.cellValue!.format.font.color = "#B91C1C";
        ruleNot.cellValue!.format.font.bold = true;
        ruleNot.cellValue!.rule = {
          formula1: '"Not reversal"',
          operator: Excel.ConditionalCellValueOperator.equalTo
        };

        // Règle 3 : "Pending" → bleu
        const rulePending = decisionCell.conditionalFormats.add(Excel.ConditionalFormatType.cellValue);
        rulePending.cellValue!.format.fill.color = "#E0E7FF";
        rulePending.cellValue!.format.font.color = "#3730A3";
        rulePending.cellValue!.format.font.bold = true;
        rulePending.cellValue!.rule = {
          formula1: '"Pending"',
          operator: Excel.ConditionalCellValueOperator.equalTo
        };
      }

      // ============ LARGEUR DES COLONNES ============
      let colIdx = 0;
      function widthCol(width: number) {
        const letter = String.fromCharCode(65 + colIdx);
        sheet.getRange(`${letter}:${letter}`).format.columnWidth = width;
        colIdx++;
      }
      widthCol(80);   // Compte
      widthCol(85);   // Date
      if (isModeSplit) {
        widthCol(100); // Débit
        widthCol(100); // Crédit
      } else {
        widthCol(110); // Montant
      }
      widthCol(240); // Libellé
      if (hasJournal) widthCol(70); // Journal
      widthCol(75);  // Line ID
      widthCol(60);  // Δ jours
      widthCol(70);  // Score
      widthCol(90);  // Bucket
      widthCol(110); // Decision

      // ============ FOOTER ============
      const footerRow = dataStartRow + dataValues.length + 2;
      sheet.getRangeByIndexes(footerRow, 0, 1, 1).values = [[`Généré par Audit Panel · ${new Date().toLocaleDateString("fr-BE", { day: "2-digit", month: "2-digit", year: "numeric" })}`]];
      const footerRange = sheet.getRangeByIndexes(footerRow, 0, 1, HEADERS.length);
      footerRange.merge();
      footerRange.format.font.name = "Aptos";
      footerRange.format.font.italic = true;
      footerRange.format.font.size = 9;
      footerRange.format.font.color = "#9CA3AF";

      // ============ CACHER GRIDLINES + GRISER L'EXTÉRIEUR ============
      sheet.gridlines = false;

      const outerGrey = "#E5E7EB";
      const allZone = sheet.getRangeByIndexes(0, 0, 500, 50);
      allZone.format.fill.color = outerGrey;

      const tableArea = sheet.getRangeByIndexes(0, 0, footerRow + 1, HEADERS.length);
      tableArea.format.fill.color = "white";

      // Réappliquer header bleu
      headerRange.format.fill.color = "#1E40AF";

      // Réappliquer couleurs Bucket et Decision
      for (let pi = 0; pi < sortedRows.length; pi++) {
        const topRowExcel = dataStartRow + (pi * 2);
        const bucketCell = sheet.getRangeByIndexes(topRowExcel, bucketColIdx, 1, 1);
        const bucket = sortedRows[pi].bucket;
        if (bucket === "CERTAIN") bucketCell.format.fill.color = "#FEE2E2";
        else if (bucket === "PROBABLE") bucketCell.format.fill.color = "#FEF3C7";
        const decisionCell = sheet.getRangeByIndexes(topRowExcel, decisionColIdx, 1, 1);
        const initialDecision = bucket === "CERTAIN" ? "Reversal" : "Pending";
        if (initialDecision === "Reversal") decisionCell.format.fill.color = "#D1FAE5";
        else if (initialDecision === "Pending") decisionCell.format.fill.color = "#E0E7FF";
      }

      // ============ BORDURE EXTERNE DU TABLEAU ============
      const tableBorderRange = sheet.getRangeByIndexes(HEADER_ROW, 0, dataValues.length + 1, HEADERS.length);
      const borders = tableBorderRange.format.borders;
      const borderColor = "#1E40AF";

      const sides = ["EdgeTop", "EdgeBottom", "EdgeLeft", "EdgeRight"];
      for (const side of sides) {
        const b = borders.getItem(side as Excel.BorderIndex);
        b.style = Excel.BorderLineStyle.continuous;
        b.color = borderColor;
        b.weight = Excel.BorderWeight.medium;
      }

      // ============ BORDURE ÉPAISSE ENTRE PAIRES ============
      for (let pi = 0; pi < sortedRows.length - 1; pi++) {
        const lastRowOfPairExcel = dataStartRow + (pi * 2) + 1;
        const pairBorderRange = sheet.getRangeByIndexes(lastRowOfPairExcel, 0, 1, HEADERS.length);
        const pb = pairBorderRange.format.borders.getItem("EdgeBottom");
        pb.style = Excel.BorderLineStyle.continuous;
        pb.color = borderColor;
        pb.weight = Excel.BorderWeight.medium;
      }

      await context.sync();


    });

    document.getElementById("rev-certain-count")!.textContent = String(s.certain_count);
    document.getElementById("rev-probable-count")!.textContent = String(s.probable_count);
    document.getElementById("rev-total")!.textContent = String(s.total_pairs);
    document.getElementById("rev-impacted")!.textContent = String(s.lines_impacted_certain + s.lines_impacted_probable);
    summaryDiv.style.display = "block";

    setRevStatus(`✓ ${s.total_pairs} reversals détectés (${s.certain_count} CERTAIN + ${s.probable_count} PROBABLE)`, "success");
    document.querySelector<HTMLElement>('.step-btn[data-screen="reversals"]')!.classList.add("completed");
  // Stocker les paires pour validation et exclusion
    reversalsState.certainIds.clear();
    reversalsState.probablePairs = [];

    for (const pair of result.rows) {
      const idA = String(pair.original_id);
      const idB = String(pair.reversal_id);
      if (pair.bucket === "CERTAIN") {
        reversalsState.certainIds.add(idA);
        reversalsState.certainIds.add(idB);
      } else if (pair.bucket === "PROBABLE") {
        reversalsState.probablePairs.push({
          ...pair,
          validation: "pending"
        });
      }
    }

    // Tri par compte croissant pour grouper les paires d'un même compte
    reversalsState.probablePairs.sort((a, b) => {
      const ca = String(a.compte ?? "");
      const cb = String(b.compte ?? "");
      if (ca < cb) return -1;
      if (ca > cb) return 1;
      // Tie-break par date originale
      const da = String(a.date_original ?? "");
      const db = String(b.date_original ?? "");
      return da.localeCompare(db);
    });

    reversalsState.probableIdx = 0;
    renderProbableValidation();

  } catch (err: any) {
    console.error(err);
    setRevStatus(`Erreur : ${err.message}`, "error");
  } finally {
    btn.disabled = false;
  }
}

// ============================================================
// ANALYTICAL REVIEW SCREEN
// ============================================================
function setARStatus(message: string, kind: "info" | "success" | "error") {
  const el = document.getElementById("ar-status")!;
  el.textContent = message;
  el.className = `status show ${kind}`;
}

function bindAnalyticalScreen() {
  const ruleAInput = document.getElementById("ar-rule-a-input") as HTMLInputElement;
  const ruleADisplay = document.getElementById("ar-rule-a-display")!;
  const ruleBInput = document.getElementById("ar-rule-b-input") as HTMLInputElement;
  const ruleBDisplay = document.getElementById("ar-rule-b-display")!;

  ruleAInput.oninput = () => {
    const v = parseFloat(ruleAInput.value);
    ruleADisplay.textContent = formatEur(v);
  };
  ruleBInput.oninput = () => {
    ruleBDisplay.textContent = `${ruleBInput.value}%`;
  };
}

function bindAnalyticalControls() {
  // Segmented controls (granularité + combinaison)
  document.querySelectorAll<HTMLElement>("#ar-granularity .segment, #ar-rule-combo .segment").forEach(seg => {
    seg.onclick = () => {
      const parent = seg.parentElement!;
      parent.querySelectorAll(".segment").forEach(s => s.classList.remove("active"));
      seg.classList.add("active");
    };
  });

  // Règle A : input numérique avec format
  const ruleAInput = document.getElementById("ar-rule-a-input") as HTMLInputElement;
  const ruleADisplay = document.getElementById("ar-rule-a-display")!;

  ruleAInput.oninput = () => {
    const v = parseNumberInput(ruleAInput.value);
    ruleADisplay.textContent = formatEur(v);
  };
  ruleAInput.onblur = () => {
    const v = parseNumberInput(ruleAInput.value);
    ruleAInput.value = formatNumberInput(v);
  };

  // Règle B : slider %
  const ruleBInput = document.getElementById("ar-rule-b-input") as HTMLInputElement;
  const ruleBDisplay = document.getElementById("ar-rule-b-display")!;
  ruleBInput.oninput = () => {
    ruleBDisplay.textContent = `${ruleBInput.value}%`;
  };
}

async function runAnalytical() {
  const btn = document.getElementById("ar-btn") as HTMLButtonElement;
  const summaryDiv = document.getElementById("ar-summary")!;
  const statusEl = document.getElementById("ar-status")!;

  const setStatus = (msg: string, kind: "info" | "success" | "error") => {
    statusEl.textContent = msg;
    statusEl.className = `status show ${kind}`;
  };

  if (!state.sheets.tb || !state.sheets.tb_n1 || !state.sheets.chart) {
    setStatus("Setup requis : TB, TB N-1 et Chart doivent être mappés.", "error");
    return;
  }

  const granularity = document.querySelector<HTMLElement>("#ar-granularity .segment.active")!.dataset.value!;
  const ruleCombination = document.querySelector<HTMLElement>("#ar-rule-combo .segment.active")!.dataset.value!;
  const ruleAThreshold = parseNumberInput((document.getElementById("ar-rule-a-input") as HTMLInputElement).value);
  const ruleBThresholdPct = parseFloat((document.getElementById("ar-rule-b-input") as HTMLInputElement).value);

  if (ruleAThreshold <= 0) {
    setStatus("Seuil règle A invalide.", "error");
    return;
  }

  btn.disabled = true;
  summaryDiv.style.display = "none";
  setStatus(`Lecture des TBs...`, "info");

  try {
    const [tbN, tbN1, chartRows] = await Promise.all([
      readMappedSheet("tb"),
      readMappedSheet("tb_n1"),
      readMappedSheet("chart"),
    ]);

    if (tbN.length === 0) throw new Error(`TB '${state.sheets.tb}' vide ou introuvable`);
    if (tbN1.length === 0) throw new Error(`TB N-1 '${state.sheets.tb_n1}' vide ou introuvable`);
    if (chartRows.length === 0) throw new Error(`Chart '${state.sheets.chart}' vide ou introuvable`);

    setStatus(`${tbN.length} comptes TB N, ${tbN1.length} comptes TB N-1, ${chartRows.length} lignes Chart. Calcul...`, "info");

    const response = await fetch(`${BACKEND_URL}/analytical-tb`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        tb_n: tbN,
        tb_n1: tbN1,
        chart: chartRows,
        granularity: granularity,
        rule_a_enabled: true,
        rule_a_threshold: ruleAThreshold,
        rule_b_enabled: true,
        rule_b_threshold_pct: ruleBThresholdPct,
        rule_combination: ruleCombination,
      }),
    });

    if (!response.ok) {
      const err = await response.text();
      throw new Error(`Backend ${response.status}: ${err}`);
    }

    const result = await response.json();
    const s = result.summary;
    setStatus(`${s.to_scope_count} flags / ${s.total_lines} lignes. Écriture...`, "info");

    await Excel.run(async (context) => {
      const sheets = context.workbook.worksheets;
      sheets.load("items/name");
      await context.sync();

      const existing = sheets.items.find((s: any) => s.name === "Analytical_Result");
      if (existing) {
        existing.delete();
        await context.sync();
      }

      const sheet = sheets.add("Analytical_Result");
      sheet.activate();

      const financialLines = result.rows;
      if (!financialLines || financialLines.length === 0) {
        sheet.getRange("A1").values = [["Aucun résultat"]];
        await context.sync();
        return;
      }

      // ============ HEADER (titre + sous-titre) ============
      const titleRange = sheet.getRange("A1");
      titleRange.values = [["Variance Analysis — N vs N-1"]];
      titleRange.format.font.name = "Aptos";
      titleRange.format.font.bold = true;
      titleRange.format.font.size = 14;
      titleRange.format.font.color = "#1E40AF";

      const subtitleRange = sheet.getRange("A2");
      const subtitleText = `Granularité : ${granularity} · Règles : Δ > €${ruleAThreshold.toLocaleString()} ${ruleCombination} Δ > ${ruleBThresholdPct}%`;
      subtitleRange.values = [[subtitleText]];
      subtitleRange.format.font.name = "Aptos";
      subtitleRange.format.font.italic = true;
      subtitleRange.format.font.size = 11;
      subtitleRange.format.font.color = "#6B7280";

      // ============ HEADERS DU TABLEAU (ligne 4) ============
      const HEADERS = ["Compte", "Libellé", "Solde N", "Solde N-1", "Δ €", "Δ %", "Explication"];
      const HEADER_ROW = 3; // ligne 4 (0-indexed)
      const headerRange = sheet.getRangeByIndexes(HEADER_ROW, 0, 1, HEADERS.length);
      headerRange.values = [HEADERS];
      headerRange.format.font.name = "Aptos";
      headerRange.format.font.bold = true;
      headerRange.format.font.size = 11;
      headerRange.format.font.color = "white";
      headerRange.format.fill.color = "#1E40AF";
      headerRange.format.horizontalAlignment = Excel.HorizontalAlignment.center;
      headerRange.format.verticalAlignment = Excel.VerticalAlignment.center;
      headerRange.format.rowHeight = 24;

      // ============ CONSTRUCTION DU TABLEAU HIERARCHIQUE ============
      // Pour chaque financial line, on calcule combien de lignes elle prend (titre + comptes + total)
      // Et on prépare une structure de blocs

      type Block = {
        flKey: string;
        toScope: boolean;
        startRow: number;  // ligne de la financial line title
        endRow: number;    // ligne du total
        rows: any[][];     // les valeurs à écrire (compte, libelle, sn, sn1, dEur, dPct)
        rowTypes: ("fl_title" | "account" | "total")[];
      };

      const blocks: Block[] = [];
      let currentRow = HEADER_ROW + 1;

      for (const fl of financialLines) {
        const block: Block = {
          flKey: fl.key,
          toScope: fl.to_scope,
          startRow: currentRow,
          endRow: 0,
          rows: [],
          rowTypes: [],
        };

        // Lignes : un compte par ligne, indenté
        for (const acc of fl.accounts) {
          block.rows.push([
            "    " + acc.compte, // indentation visuelle
            acc.libelle,
            acc.value_n,
            acc.value_n1,
            acc.variation_eur,
            acc.variation_pct / 100, // converti en décimal pour format Excel %
          ]);
          block.rowTypes.push("account");
        }

        // Ligne total
        block.rows.push([
          "Total " + fl.label,
          "",
          fl.value_n,
          fl.value_n1,
          fl.variation_eur,
          fl.variation_pct / 100,
        ]);
        block.rowTypes.push("total");

        block.endRow = currentRow + block.rows.length - 1;
        blocks.push(block);
        currentRow += block.rows.length;
      }

      // ============ ECRITURE DES VALEURS ============
      // On écrit toutes les lignes en une fois pour la performance, puis on style par bloc
      const allValues: any[][] = [];
      for (const b of blocks) {
        for (const row of b.rows) {
          allValues.push([...row, ""]); // colonne explication vide (sera fusionnée et stylée plus tard)
        }
      }

      const dataStartRow = HEADER_ROW + 1;
      const dataRange = sheet.getRangeByIndexes(dataStartRow, 0, allValues.length, HEADERS.length);
      dataRange.values = allValues;

      // ============ STYLING PAR BLOC ============
      for (const b of blocks) {
        const blockRange = sheet.getRangeByIndexes(b.startRow, 0, b.rows.length, HEADERS.length);
        blockRange.format.font.name = "Aptos";
        blockRange.format.font.size = 10;

        // Bordure top sur la première ligne du bloc (entre financial lines)
        const topBorder = sheet.getRangeByIndexes(b.startRow, 0, 1, HEADERS.length).format.borders.getItem("EdgeTop");
        topBorder.style = Excel.BorderLineStyle.continuous;
        topBorder.color = "#6B7280";
        topBorder.weight = Excel.BorderWeight.medium;

        // Style de chaque ligne du bloc selon son type
        for (let i = 0; i < b.rows.length; i++) {
          const rowType = b.rowTypes[i];
          const rowRange = sheet.getRangeByIndexes(b.startRow + i, 0, 1, HEADERS.length);

          if (rowType === "fl_title") {
            rowRange.format.font.bold = true;
            rowRange.format.font.color = "#1E40AF";
            rowRange.format.fill.color = "#EFF6FF";
          } else if (rowType === "total") {
            rowRange.format.font.bold = true;
            // Bordure top sur la ligne total
            const totalBorder = rowRange.format.borders.getItem("EdgeTop");
            totalBorder.style = Excel.BorderLineStyle.continuous;
            totalBorder.color = "#D1D5DB";
            totalBorder.weight = Excel.BorderWeight.thin;
          }
          // accounts : style par défaut (regular, sans modification)
        }

        // ============ FUSION DE LA CELLULE EXPLICATION ============
        // Colonne 6 (index) = "Explication", à fusionner verticalement sur toutes les lignes du bloc
        const explainCol = HEADERS.length - 1; // 6
        const explainRange = sheet.getRangeByIndexes(b.startRow, explainCol, b.rows.length, 1);
        explainRange.merge();
        explainRange.format.wrapText = true;
        explainRange.format.verticalAlignment = Excel.VerticalAlignment.top;
        explainRange.format.horizontalAlignment = Excel.HorizontalAlignment.left;
        explainRange.format.font.name = "Aptos";
        explainRange.format.font.size = 10;

        if (!b.toScope) {
          // Non significatif : juste grisé, pas de texte
          explainRange.format.fill.color = "#F3F4F6";
        } else {
          // À expliquer : fond pâle (cellule vide pour que l'utilisateur écrive son explication)
          explainRange.format.fill.color = "#FFFBEB";
        }
      }

      // ============ FORMAT DES NOMBRES ============
      // Colonnes Solde N (2), Solde N-1 (3), Δ € (4) : format avec parenthèses pour négatifs
      const numFormat = '#,##0.00;(#,##0.00)';
      for (const colIdx of [2, 3, 4]) {
        const totalDataRows = allValues.length;
        const colRange = sheet.getRangeByIndexes(dataStartRow, colIdx, totalDataRows, 1);
        colRange.numberFormat = Array(totalDataRows).fill([numFormat]);
      }

      // Colonne Δ % (5) : format pourcentage
      const pctRange = sheet.getRangeByIndexes(dataStartRow, 5, allValues.length, 1);
      pctRange.numberFormat = Array(allValues.length).fill(["0.0%;(0.0%)"]);

      // ============ LARGEUR DES COLONNES ============
      sheet.getRange("A:A").format.columnWidth = 75;  // Compte
      sheet.getRange("B:B").format.columnWidth = 220; // Libellé
      sheet.getRange("C:C").format.columnWidth = 105; // Solde N
      sheet.getRange("D:D").format.columnWidth = 105; // Solde N-1
      sheet.getRange("E:E").format.columnWidth = 95;  // Δ €
      sheet.getRange("F:F").format.columnWidth = 65;  // Δ %
      sheet.getRange("G:G").format.columnWidth = 280; // Explication

      // ============ HAUTEUR DES LIGNES ============
      // Augmenter la hauteur pour les lignes avec cellule fusionnée (au moins celles qu'il faut)
      // Approximation : les blocs avec to_scope ont besoin de plus d'espace pour l'explication
      for (const b of blocks) {
        if (b.toScope) {
          const minHeight = Math.max(60, b.rows.length * 20);
          // Note : la hauteur effective est gérée via la fusion + wrap text
        }
      }

      // ============ FOOTER (metadata) ============
      const footerRow = dataStartRow + allValues.length + 2;
      sheet.getRangeByIndexes(footerRow, 0, 1, 1).values = [[`Généré par Audit Panel · ${new Date().toLocaleDateString("fr-BE", { day: "2-digit", month: "2-digit", year: "numeric" })}`]];
      const footerRange = sheet.getRangeByIndexes(footerRow, 0, 1, HEADERS.length);
      footerRange.merge();
      footerRange.format.font.name = "Aptos";
      footerRange.format.font.italic = true;
      footerRange.format.font.size = 9;
      footerRange.format.font.color = "#9CA3AF";

      // ============ CACHER LES GRIDLINES ============
      sheet.gridlines = false;

      // ============ GRISAGE DES ZONES HORS TABLEAU ============
      // Couleur extérieure : plus foncée que celle des cellules "non significatives" pour différencier
      const outerGrey = "#E5E7EB"; // gris légèrement plus foncé que #F3F4F6 utilisé pour les non significatifs

      // Grise tout : on commence par griser une grosse zone, puis on remet blanc sur le tableau
      const allZone = sheet.getRangeByIndexes(0, 0, 500, 50);
      allZone.format.fill.color = outerGrey;

      // Remettre blanc sur la zone du tableau (header titre + headers tableau + data + footer)
      const tableArea = sheet.getRangeByIndexes(0, 0, footerRow + 1, HEADERS.length);
      tableArea.format.fill.color = "white";

      // Réappliquer les couleurs spécifiques (header bleu + cellules non sig grisées + financial line bleu pâle + à expliquer jaune pâle)
      // Header tableau
      const headerRangeRedo = sheet.getRangeByIndexes(HEADER_ROW, 0, 1, HEADERS.length);
      headerRangeRedo.format.fill.color = "#1E40AF";

      // Re-styler les cellules d'explication (grisée si non significatif, jaune pâle si à expliquer)
      for (const b of blocks) {
        const explainCol = HEADERS.length - 1;
        const explainRangeRedo = sheet.getRangeByIndexes(b.startRow, explainCol, b.rows.length, 1);
        if (!b.toScope) {
          explainRangeRedo.format.fill.color = "#F3F4F6"; // gris cellules non sig
        } else {
          explainRangeRedo.format.fill.color = "#FFFBEB"; // jaune pâle à expliquer
        }
      }

      // ============ BORDURE EXTERNE DU TABLEAU ============
      const tableBorderRange = sheet.getRangeByIndexes(HEADER_ROW, 0, allValues.length + 1, HEADERS.length);
      const borders = tableBorderRange.format.borders;
      const borderColor = "#1E40AF";

      const edgeTop = borders.getItem("EdgeTop");
      edgeTop.style = Excel.BorderLineStyle.continuous;
      edgeTop.color = borderColor;
      edgeTop.weight = Excel.BorderWeight.medium;

      const edgeBottom = borders.getItem("EdgeBottom");
      edgeBottom.style = Excel.BorderLineStyle.continuous;
      edgeBottom.color = borderColor;
      edgeBottom.weight = Excel.BorderWeight.medium;

      const edgeLeft = borders.getItem("EdgeLeft");
      edgeLeft.style = Excel.BorderLineStyle.continuous;
      edgeLeft.color = borderColor;
      edgeLeft.weight = Excel.BorderWeight.medium;

      const edgeRight = borders.getItem("EdgeRight");
      edgeRight.style = Excel.BorderLineStyle.continuous;
      edgeRight.color = borderColor;
      edgeRight.weight = Excel.BorderWeight.medium;

      await context.sync();
    });

    document.getElementById("ar-flag")!.textContent = String(s.to_scope_count);
    document.getElementById("ar-noflag")!.textContent = String(s.no_flag_count);
    document.getElementById("ar-total")!.textContent = String(s.total_lines);
    document.getElementById("ar-gran")!.textContent = granularity;
    summaryDiv.style.display = "block";

    setStatus(`✓ ${s.to_scope_count} flags sur ${s.total_lines} lignes`, "success");
    document.querySelector<HTMLElement>('.step-btn[data-screen="analytical"]')!.classList.add("completed");
  } catch (err: any) {
    console.error(err);
    setStatus(`Erreur : ${err.message}`, "error");
  } finally {
    btn.disabled = false;
  }
}

// ============================================================
// SAMPLING AUTO MODE — sections from Scoping
// ============================================================

interface SectionState {
  name: string;
  accounts: string[];
  population_count: number;
  population_value: number;
  checked: boolean;
  method: string;
  risk: string;
  custom_n: number | null;
  seed: number;
}

const samplingState = {
  sections: [] as Array<{
    name: string;
    accounts: string[];
    population_value: number;
    population_count: number;
    method: string;
    risk: string;
    custom_n: number | null;
    seed: number;
    checked: boolean;
    expanded: boolean;
    estimated_n: number;
    last_run_n: number | null;
    last_run_risk: string | null;
    last_run_excluded_accounts: string[] | null;
    excluded_accounts: Set<string>;
  }>,
  chart: [] as any[],
};

function bindSamplingScreen() {
  // Mode toggle
  
}

async function loadScopingSections() {
  const btn = document.getElementById("sample-load-sections-btn") as HTMLButtonElement;
  const container = document.getElementById("sample-sections-container")!;
  const batchAction = document.getElementById("sample-batch-action")!;

  const glSheet = state.sheets.gl!;
  const chartSheet = state.sheets.chart!;

  // Lire la granularité depuis Scoping_Result A1
  let granularity = "statutory";
  try {
    const titleVal = await Excel.run(async (context) => {
      const sheet = context.workbook.worksheets.getItem("Scoping_Result");
      const cell = sheet.getRange("A1");
      cell.load("values");
      await context.sync();
      return String(cell.values[0][0] || "");
    });
    if (titleVal.toLowerCase().includes("abcotd")) {
      throw new Error("Le Scoping a été fait en ABCOTD. Le Sampling exige Statutory. Refais le Scoping en Statutory.");
    }
  } catch (err: any) {
    if (err.message && err.message.includes("ABCOTD")) {
      throw err;
    }
    // Si pas de Scoping_Result encore, on garde Statutory par défaut
  }

  btn.disabled = true;
  container.innerHTML = '<p class="hint">Lecture du Scoping_Result...</p>';
  batchAction.style.display = "none";

  try {
    // Lire l'onglet Scoping_Result (créé par l'étape Scoping)
    if (!scopingState.loaded || scopingState.rows.length === 0) {
      throw new Error("Scoping non lancé. Lance d'abord l'étape 3 (Scoping).");
    }
    const scopingRows = scopingState.rows;

    // Trouver les colonnes pertinentes (peuvent varier selon ce que retourne le backend Scoping)
    const sample = scopingRows[0];
    const lineKey = "financial_line" in sample ? "financial_line"
                  : "label" in sample ? "label"
                  : Object.keys(sample)[0];
    const inScopeKey = "in_scope" in sample ? "in_scope" : null;

    // Filtrer les sections in scope
    const inScopeRows = inScopeKey
      ? scopingRows.filter((r: any) => r[inScopeKey] === true || r[inScopeKey] === "TRUE")
      : scopingRows;

    if (inScopeRows.length === 0) {
      throw new Error("Aucune section in scope dans Scoping_Result.");
    }

    // Lire le Chart pour mapper sections → comptes
    const chartRows = await readMappedSheet("chart");
    if (chartRows.length === 0) throw new Error(`Feuille '${chartSheet}' introuvable.`);

    samplingState.chart = chartRows;
    // Construire un map compte → libellé pour affichage dans la dropdown comptes
    const accountLabels = new Map<string, string>();
    for (const c of chartRows) {
      const compte = String(c["compte"] ?? "");
      const libelle = String(c["libelle_compte"] ?? c["libelle"] ?? "");
      if (compte) accountLabels.set(compte, libelle);
    }
    (samplingState as any).accountLabels = accountLabels;
    // Pour chaque section in scope, identifier les comptes
    const granularityCol = granularity === "statutory" ? "financial_line_statutory" : "financial_line_abcotd";
    const sections: SectionState[] = inScopeRows.map((row: any) => {
      const sectionName = String(row[lineKey]);
      const accounts = chartRows
        .filter((c: any) => String(c[granularityCol]) === sectionName)
        .map((c: any) => String(c["compte"]));

      // Récupérer la valeur depuis le scoping si présente
      const popValue = parseFloat(row["abs_value"] ?? row["value"] ?? row["total_value"] ?? "0") || 0;

      return {
        name: sectionName,
        accounts: accounts,
        population_count: accounts.length,
        population_value: popValue,
        checked: true,
        expanded: false,
        method: "MUS",
        risk: "low_risk",
        custom_n: null,
        seed: 42,
        estimated_n: 0,
        last_run_n: null,
        last_run_risk: null,
        last_run_excluded_accounts: null,
        excluded_accounts: new Set<string>(),
      };
    });

    samplingState.sections = sections;
    renderSamplingSections();
    batchAction.style.display = "block";

    setStatus(`✓ ${sections.length} sections in scope chargées`, "success");
  } catch (err: any) {
    console.error(err);
    container.innerHTML = `<p class="hint" style="color:var(--danger)">Erreur : ${err.message}</p>`;
    setStatus(`Erreur : ${err.message}`, "error");
  } finally {
    btn.disabled = false;
  }
}

function renderSamplingSections() {
  const container = document.getElementById("sample-sections-container")!;
  if (samplingState.sections.length === 0) {
    container.innerHTML = '<p class="hint">Aucune section in scope. Lance d\'abord le scoping.</p>';
    return;
  }

  container.innerHTML = samplingState.sections.map((s, idx) => {
    // Détecter si modifications depuis dernier run
    const hasChanged = s.last_run_n !== null && (
      s.last_run_risk !== s.risk ||
      JSON.stringify([...s.excluded_accounts].sort()) !== JSON.stringify((s.last_run_excluded_accounts ?? []).sort())
    );

    // Affichage du sample size
    let nLabel: string;
    if (s.last_run_n !== null && !hasChanged) {
      nLabel = `n=${s.last_run_n}`;
    } else if (s.last_run_n !== null && hasChanged) {
      nLabel = `n=? <span class="stale-indicator" title="Paramètres modifiés depuis le dernier run">⟳</span>`;
    } else {
      nLabel = `n=–`;
    }

    const popLabel = formatEur(s.population_value);
    const excludedCount = s.excluded_accounts.size;
    const excludedLabel = excludedCount > 0 ? ` · ${excludedCount} exclu${excludedCount > 1 ? 's' : ''}` : '';

    return `
      <div class="sample-card ${s.expanded ? 'expanded' : ''} ${s.checked ? '' : 'unchecked'}" data-idx="${idx}">
        <div class="sample-card-header">
          <input type="checkbox" class="sample-card-check" ${s.checked ? 'checked' : ''} data-idx="${idx}">
          <span class="sample-card-arrow">${s.expanded ? '▼' : '▶'}</span>
          <div class="sample-card-titleblock">
            <span class="sample-card-title">${formatLabel(s.name)}</span>
            <span class="sample-card-meta">${nLabel} · pop=${popLabel}${excludedLabel}</span>
          </div>
        </div>
        ${s.expanded ? `
        <div class="sample-card-body">
          <div class="sample-card-field">
            <span class="sample-card-key">Méthode</span>
            <select class="sample-card-method" data-idx="${idx}">
              <option value="MUS" ${s.method === 'MUS' ? 'selected' : ''}>MUS</option>
              <option value="Random" ${s.method === 'Random' ? 'selected' : ''}>Random</option>
              <option value="All_above_PM" ${s.method === 'All_above_PM' ? 'selected' : ''}>All > PM</option>
              <option value="Full" ${s.method === 'Full' ? 'selected' : ''}>100%</option>
            </select>
          </div>
          <div class="sample-card-field">
            <span class="sample-card-key">Risk</span>
            <select class="sample-card-risk" data-idx="${idx}">
              <option value="low_risk" ${s.risk === 'low_risk' ? 'selected' : ''}>Low</option>
              <option value="moderate_risk" ${s.risk === 'moderate_risk' ? 'selected' : ''}>Moderate</option>
              <option value="high_risk" ${s.risk === 'high_risk' ? 'selected' : ''}>High</option>
            </select>
          </div>
          <div class="sample-card-field">
            <span class="sample-card-key">Sample size</span>
            <span class="sample-card-value">${nLabel.replace('n=', '')}</span>
          </div>
          <div class="sample-card-field">
            <span class="sample-card-key">Comptes</span>
            <button type="button" class="sample-card-accounts-btn" data-idx="${idx}">
              ${s.accounts.length - excludedCount} sélectionné${(s.accounts.length - excludedCount) > 1 ? 's' : ''} ▾
            </button>
          </div>
          <div class="sample-card-accounts-dropdown" id="sample-accounts-dd-${idx}" style="display:none">
            ${s.accounts.map(acc => {
              const label = (samplingState as any).accountLabels?.get(acc) ?? '';
              return `
                <label class="account-option">
                  <input type="checkbox" data-idx="${idx}" data-account="${acc}" ${s.excluded_accounts.has(acc) ? '' : 'checked'}>
                  <span class="account-num">${acc}</span>
                  <span class="account-label">${label}</span>
                </label>
              `;
            }).join('')}
          </div>
        </div>
        ` : ''}
      </div>
    `;
  }).join('');

  bindSampleCardEvents();
}

function bindSampleCardEvents() {
  // Click header pour toggle expand (mais pas sur checkbox)
  document.querySelectorAll<HTMLElement>('.sample-card-header').forEach(header => {
    header.onclick = (e) => {
      const target = e.target as HTMLElement;
      if (target.classList.contains('sample-card-check')) return;
      const card = header.closest('.sample-card') as HTMLElement;
      const idx = parseInt(card.dataset.idx!, 10);
      samplingState.sections.forEach((s, i) => {
        s.expanded = i === idx ? !s.expanded : false;
      });
      renderSamplingSections();
    };
  });

  document.querySelectorAll<HTMLInputElement>('.sample-card-check').forEach(cb => {
    cb.onchange = () => {
      const idx = parseInt(cb.dataset.idx!, 10);
      samplingState.sections[idx].checked = cb.checked;
      renderSamplingSections();
    };
  });

  document.querySelectorAll<HTMLSelectElement>('.sample-card-method').forEach(sel => {
    sel.onchange = () => {
      const idx = parseInt(sel.dataset.idx!, 10);
      samplingState.sections[idx].method = sel.value;
      renderSamplingSections();
    };
  });

  document.querySelectorAll<HTMLSelectElement>('.sample-card-risk').forEach(sel => {
    sel.onchange = () => {
      const idx = parseInt(sel.dataset.idx!, 10);
      samplingState.sections[idx].risk = sel.value;
      renderSamplingSections();
    };
  });

  document.querySelectorAll<HTMLButtonElement>('.sample-card-accounts-btn').forEach(btn => {
    btn.onclick = (e) => {
      e.stopPropagation();
      const idx = btn.dataset.idx!;
      const dd = document.getElementById(`sample-accounts-dd-${idx}`)!;
      dd.style.display = dd.style.display === 'none' ? 'block' : 'none';
    };
  });

  document.querySelectorAll<HTMLInputElement>('.account-option input').forEach(cb => {
    cb.onchange = () => {
      const idx = parseInt(cb.dataset.idx!, 10);
      const account = cb.dataset.account!;
      const sec = samplingState.sections[idx];
      if (cb.checked) {
        sec.excluded_accounts.delete(account);
      } else {
        sec.excluded_accounts.add(account);
      }
      renderSamplingSections();
    };
  });
}

async function runBatchSampling() {
  const btn = document.getElementById("sample-batch-btn") as HTMLButtonElement;
  const checkedSections = samplingState.sections.filter(s => s.checked);

  if (checkedSections.length === 0) {
    setStatus("Aucune section cochée", "error");
    return;
  }

  const pm = getPM();
  if (pm <= 0) {
    setStatus("PM invalide (revoir Setup)", "error");
    return;
  }

  const glSheet = state.sheets.gl!;
  const openingJournals = getSelectedJournals("recon-journals");

  btn.disabled = true;
  setStatus(`Lecture du GL ${glSheet}...`, "info");

  try {
    const glLines = await readMappedSheet("gl");
    if (glLines.length === 0) throw new Error(`Feuille '${glSheet}' vide`);

    // Exclusion des reversals si toggle activé
    let workingLines = glLines;
    if (reversalsState.excludeFromSampling) {
      const excludeIds = new Set<string>();
      try {
        await Excel.run(async (context) => {
          const sheets = context.workbook.worksheets;
          sheets.load("items/name");
          await context.sync();
          const exists = sheets.items.some((s: any) => s.name === "Reversals_Result");
          if (!exists) {
            console.warn("Reversals_Result non trouvé, pas d'exclusion reversals");
            return;
          }
          const sheet = context.workbook.worksheets.getItem("Reversals_Result");
          const usedRange = sheet.getUsedRange();
          usedRange.load(["values"]);
          await context.sync();
          const values = usedRange.values;

          // Trouver headers
          let headerRowIdx = -1;
          for (let r = 0; r < Math.min(values.length, 10); r++) {
            const row = values[r];
            if (row.includes("Decision") && row.includes("Line ID")) {
              headerRowIdx = r;
              break;
            }
          }
          if (headerRowIdx === -1) {
            console.warn("Headers introuvables dans Reversals_Result");
            return;
          }
          const headers = values[headerRowIdx];
          const lineIdColIdx = headers.indexOf("Line ID");
          const decisionColIdx = headers.indexOf("Decision");
          const bucketColIdx = headers.indexOf("Bucket");
          if (lineIdColIdx === -1 || decisionColIdx === -1) {
            console.warn("Reversals_Result n'a pas de colonne 'Decision' ou 'Line ID'. Lance 'Appliquer les décisions' d'abord.");
            return;
          }

          // Parcours 2 lignes par paire : la décision est sur la 1ère ligne (mergée)
          // On exclut les 2 lignes (original + reversal) si decision = "Reversal"
          const dataStartRow = headerRowIdx + 1;
          for (let i = dataStartRow; i < values.length; i += 2) {
            if (i + 1 >= values.length) break;
            const topRow = values[i];
            const bottomRow = values[i + 1];
            // Si on est sur le footer, on arrête
            if (!topRow[bucketColIdx] && !bottomRow[bucketColIdx]) break;
            const decision = String(topRow[decisionColIdx] || "").trim();
            if (decision === "Reversal") {
              const lineIdTop = String(topRow[lineIdColIdx] || "").trim();
              const lineIdBottom = String(bottomRow[lineIdColIdx] || "").trim();
              if (lineIdTop) excludeIds.add(lineIdTop);
              if (lineIdBottom) excludeIds.add(lineIdBottom);
            }
          }
        });
      } catch (err) {
        console.warn("Reversals_Result non trouvé, pas d'exclusion reversals");
      }
      const beforeCount = workingLines.length;
      workingLines = workingLines.filter((l: any) => !excludeIds.has(String(l["id"] ?? l["ligne_id"] ?? "")));
      const excludedCount = beforeCount - workingLines.length;
      setStatus(`Exclusion reversals (depuis Reversals_Result) : ${excludedCount} lignes retirées. ${workingLines.length} lignes envoyées (${checkedSections.length} sections, PM ${formatEur(pm)})...`, "info");
    } else {
      setStatus(`${workingLines.length} lignes envoyées (${checkedSections.length} sections, PM ${formatEur(pm)})...`, "info");
    }
    const response = await fetch(`${BACKEND_URL}/sample-batch`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        lines: workingLines,
        pm: pm,
        sections: checkedSections.map(s => ({
          name: s.name,
          accounts: s.accounts.filter(acc => !s.excluded_accounts.has(acc)),
          method: s.method,
          risk_level: s.risk,
          seed: s.seed,
          custom_n: s.custom_n,
        })),
        opening_journals: openingJournals,
      }),
    });
    if (!response.ok) {
      const err = await response.text();
      throw new Error(`Backend ${response.status}: ${err}`);
    }

    const result = await response.json();
    const s = result.summary;
    setStatus(`${s.total_sample_count} lignes au total. Écriture des onglets...`, "info");

    // Écrire un onglet par section
    await Excel.run(async (context) => {
      const sheets = context.workbook.worksheets;
      sheets.load("items/name");
      await context.sync();

      const SELECTION_LABELS: Record<string, string> = {
        "FULL_TESTING": "Full testing",
        "TOP_STRATUM_ABOVE_PM": "Key item",
        "TOP_STRATUM_100PCT": "Key item",
        "RANDOM_MUS": "MUS PPS",
        "RANDOM": "Random",
      };

      for (const secResult of result.sections_results) {
        const sheetName = `Sample_${secResult.section_name}`.substring(0, 31);

        const existing = sheets.items.find((sh: any) => sh.name === sheetName);
        if (existing) {
          existing.delete();
          await context.sync();
        }

        const sheet = sheets.add(sheetName);
        const sample = secResult.sample;

        // ============ HEADER (titre + sous-titre) ============
        const titleRange = sheet.getRange("A1");
        titleRange.values = [[`Sample — ${formatLabel(secResult.section_name)}`]];
        titleRange.format.font.name = "Aptos";
        titleRange.format.font.bold = true;
        titleRange.format.font.size = 14;
        titleRange.format.font.color = "#1E40AF";

        const sm = secResult.summary;
        const subtitleRange = sheet.getRange("A2");
        const subtitleText = secResult.error
          ? `Erreur : ${secResult.error}`
          : `Méthode ${secResult.method} · Population ${sm.population_count} lignes (${formatEur(sm.population_value)}) · Sample ${sm.sample_count} (Key items ${sm.top_stratum_count}, MUS ${sm.random_sample_count}) · Coverage ${sm.coverage_pct?.toFixed(1) ?? 0}%`;
        subtitleRange.values = [[subtitleText]];
        subtitleRange.format.font.name = "Aptos";
        subtitleRange.format.font.italic = true;
        subtitleRange.format.font.size = 11;
        subtitleRange.format.font.color = secResult.error ? "#B91C1C" : "#6B7280";

        if (sample.length === 0) {
          await context.sync();
          continue;
        }

        // ============ HEADERS DU TABLEAU ============
        const headers = Object.keys(sample[0]);
        const HEADER_ROW = 3;
        const headerRange = sheet.getRangeByIndexes(HEADER_ROW, 0, 1, headers.length);
        headerRange.values = [headers];
        headerRange.format.font.name = "Aptos";
        headerRange.format.font.bold = true;
        headerRange.format.font.size = 11;
        headerRange.format.font.color = "white";
        headerRange.format.fill.color = "#1E40AF";
        headerRange.format.horizontalAlignment = Excel.HorizontalAlignment.center;
        headerRange.format.verticalAlignment = Excel.VerticalAlignment.center;
        headerRange.format.rowHeight = 24;

        // ============ DATA (avec traduction selection_type) ============
        const dataValues = sample.map((row: any) =>
          headers.map((h) => {
            const v = row[h];
            if (h === "selection_type" && typeof v === "string") return SELECTION_LABELS[v] ?? v;
            if (typeof v === "boolean") return v ? "Oui" : "Non";
            if (typeof v === "number") return Math.round(v * 100) / 100;
            return v ?? "";
          })
        );
        const dataStartRow = HEADER_ROW + 1;
        const dataRange = sheet.getRangeByIndexes(dataStartRow, 0, dataValues.length, headers.length);
        dataRange.values = dataValues;
        dataRange.format.font.name = "Aptos";
        dataRange.format.font.size = 10;

        // ============ FORMAT NOMBRES ET DATES ============
        const numFormat = '#,##0.00;(#,##0.00)';
        const numericKeywords = ["debit", "credit", "montant", "value", "amount", "solde"];
        for (let colIdx = 0; colIdx < headers.length; colIdx++) {
          const colName = headers[colIdx].toLowerCase();
          if (numericKeywords.some(nc => colName.includes(nc))) {
            const colRange = sheet.getRangeByIndexes(dataStartRow, colIdx, dataValues.length, 1);
            colRange.numberFormat = Array(dataValues.length).fill([numFormat]);
          }
          if (colName === "date" || colName.endsWith("_date")) {
            const colRange = sheet.getRangeByIndexes(dataStartRow, colIdx, dataValues.length, 1);
            colRange.numberFormat = Array(dataValues.length).fill(["dd/mm/yyyy"]);
          }
        }

        // ============ AUTOFIT (largeurs adaptées au contenu) ============
        sheet.getUsedRange().format.autofitColumns();

        // ============ FOOTER ============
        const footerRow = dataStartRow + dataValues.length + 2;
        sheet.getRangeByIndexes(footerRow, 0, 1, 1).values = [[`Généré par Audit Panel · ${new Date().toLocaleDateString("fr-BE", { day: "2-digit", month: "2-digit", year: "numeric" })}`]];
        const footerRange = sheet.getRangeByIndexes(footerRow, 0, 1, headers.length);
        footerRange.merge();
        footerRange.format.font.name = "Aptos";
        footerRange.format.font.italic = true;
        footerRange.format.font.size = 9;
        footerRange.format.font.color = "#9CA3AF";

        // ============ CACHER GRIDLINES + GRISER L'EXTÉRIEUR ============
        sheet.gridlines = false;

        const outerGrey = "#E5E7EB";
        const allZone = sheet.getRangeByIndexes(0, 0, 500, 50);
        allZone.format.fill.color = outerGrey;

        const tableArea = sheet.getRangeByIndexes(0, 0, footerRow + 1, headers.length);
        tableArea.format.fill.color = "white";

        // Réappliquer le header bleu
        headerRange.format.fill.color = "#1E40AF";

        // ============ BORDURE EXTERNE DU TABLEAU ============
        const tableBorderRange = sheet.getRangeByIndexes(HEADER_ROW, 0, dataValues.length + 1, headers.length);
        const borders = tableBorderRange.format.borders;
        const borderColor = "#1E40AF";

        const edgeTop = borders.getItem("EdgeTop");
        edgeTop.style = Excel.BorderLineStyle.continuous;
        edgeTop.color = borderColor;
        edgeTop.weight = Excel.BorderWeight.medium;

        const edgeBottom = borders.getItem("EdgeBottom");
        edgeBottom.style = Excel.BorderLineStyle.continuous;
        edgeBottom.color = borderColor;
        edgeBottom.weight = Excel.BorderWeight.medium;

        const edgeLeft = borders.getItem("EdgeLeft");
        edgeLeft.style = Excel.BorderLineStyle.continuous;
        edgeLeft.color = borderColor;
        edgeLeft.weight = Excel.BorderWeight.medium;

        const edgeRight = borders.getItem("EdgeRight");
        edgeRight.style = Excel.BorderLineStyle.continuous;
        edgeRight.color = borderColor;
        edgeRight.weight = Excel.BorderWeight.medium;

        await context.sync();
      }
    });

    // Marquer les sections lancées comme "à jour" (pour ne plus afficher l'indicateur ⟳)
    for (const s of checkedSections) {
      const sectionResult = result.sections_results.find((r: any) => r.section_name === s.name);
      if (sectionResult) {
        s.last_run_n = sectionResult.summary?.sample_count ?? sectionResult.sample_count ?? 0;
        s.last_run_risk = s.risk;
        s.last_run_excluded_accounts = [...s.excluded_accounts];
      }
    }
    renderSamplingSections();
    setStatus(`✓ ${s.successful_sections}/${s.total_sections} sections OK · ${s.total_sample_count} lignes au total`, "success");
    document.querySelector<HTMLElement>('.step-btn[data-screen="sampling"]')!.classList.add("completed");
  } catch (err: any) {
    console.error(err);
    setStatus(`Erreur : ${err.message}`, "error");
  } finally {
    btn.disabled = false;
  }
}

// ============================================================
// EXPORT SCREEN
// ============================================================
function setExportStatus(message: string, kind: "info" | "success" | "error") {
  const el = document.getElementById("export-status")!;
  el.textContent = message;
  el.className = `status show ${kind}`;
}

// Inventaire des onglets actuels du classeur
async function detectSheets(): Promise<{
  inputs: string[];
  results: string[];
  samples: string[];
  recap: string[];
  memo: string[];
  others: string[];
}> {
  return await Excel.run(async (context) => {
    const sheets = context.workbook.worksheets;
    sheets.load("items/name");
    await context.sync();

    const inputs: string[] = [];
    const results: string[] = [];
    const samples: string[] = [];
    const recap: string[] = [];
    const memo: string[] = [];
    const others: string[] = [];

    for (const sh of sheets.items) {
      const n = sh.name;
      if (n === "00_Recap") recap.push(n);
      else if (n === "ZZ_Memo") memo.push(n);
      else if (n.startsWith("Sample_")) samples.push(n);
      else if (n.endsWith("_Result") || n === "Recon_Result" || n === "Scoping_Result" || n === "Reversals_Result" || n === "Analytical_Result") {
        results.push(n);
      } else if (n === "GL" || n === "TB" || n === "Chart" || n === "tb_n1" || n.toLowerCase().includes("gl") || n.toLowerCase().includes("chart")) {
        inputs.push(n);
      } else {
        others.push(n);
      }
    }
    return { inputs, results, samples, recap, memo, others };
  });
}

async function showDetectedSheets() {
  const btn = document.getElementById("export-detect-btn") as HTMLButtonElement;
  const list = document.getElementById("export-detected-list")!;
  btn.disabled = true;
  list.textContent = "Lecture...";

  try {
    const det = await detectSheets();
    list.innerHTML = `
      <div><strong>Inputs</strong> (${det.inputs.length}) : ${det.inputs.join(", ") || "—"}</div>
      <div><strong>Résultats</strong> (${det.results.length}) : ${det.results.join(", ") || "—"}</div>
      <div><strong>Samples</strong> (${det.samples.length}) : ${det.samples.join(", ") || "—"}</div>
      <div><strong>Recap existant</strong> : ${det.recap.length > 0 ? "oui (sera écrasé)" : "non"}</div>
      <div><strong>Memo existant</strong> : ${det.memo.length > 0 ? "oui (sera écrasé)" : "non"}</div>
      ${det.others.length > 0 ? `<div><strong>Autres</strong> : ${det.others.join(", ")}</div>` : ""}
    `;
    setExportStatus(`✓ ${det.inputs.length + det.results.length + det.samples.length + det.recap.length + det.memo.length + det.others.length} onglets détectés`, "info");
  } catch (err: any) {
    setExportStatus(`Erreur : ${err.message}`, "error");
  } finally {
    btn.disabled = false;
  }
}

async function generateExport() {
  const btn = document.getElementById("export-generate-btn") as HTMLButtonElement;
  const summaryDiv = document.getElementById("export-summary")!;

  btn.disabled = true;
  summaryDiv.style.display = "none";
  setExportStatus("Génération du récap...", "info");

  try {
    const det = await detectSheets();

    await Excel.run(async (context) => {
      const sheets = context.workbook.worksheets;
      sheets.load("items/name");
      await context.sync();

      // === 1. Supprimer les anciens 00_Recap et ZZ_Memo s'ils existent ===
      const oldRecap = sheets.items.find((s: any) => s.name === "00_Recap");
      if (oldRecap) {
        oldRecap.delete();
        await context.sync();
      }
      const oldMemo = sheets.items.find((s: any) => s.name === "ZZ_Memo");
      if (oldMemo) {
        oldMemo.delete();
        await context.sync();
      }

      // === 2. Créer 00_Recap ===
      const recap = sheets.add("00_Recap");
      const today = new Date().toLocaleDateString("fr-BE");

      // Titre principal
      const titleR = recap.getRange("A1");
      titleR.values = [["Audit Panel — Récapitulatif mission"]];
      titleR.format.font.bold = true;
      titleR.format.font.size = 18;
      titleR.format.font.color = "#0F172A";

      // Sous-titre
      const subR = recap.getRange("A2");
      subR.values = [[`Généré le ${today}`]];
      subR.format.font.italic = true;
      subR.format.font.color = "#71717A";
      subR.format.font.size = 11;

      // Section "Materiality"
      const matHeader = recap.getRange("A4");
      matHeader.values = [["MATÉRIALITÉ"]];
      matHeader.format.font.bold = true;
      matHeader.format.font.size = 11;
      matHeader.format.font.color = "white";
      matHeader.format.fill.color = "#0F172A";

      const matRows = [
        ["Materiality globale", `€${state.materiality.toLocaleString()}`],
        ["Performance Materiality (PM)", `€${getPM().toLocaleString()} (${state.pmPct}% de Materiality)`],
        ["Clearly Trivial Threshold (CTT)", `€${getCTT().toLocaleString()} (${state.cttPct}% de Materiality)`],
      ];
      const matRange = recap.getRangeByIndexes(4, 0, matRows.length, 2);
      matRange.values = matRows;
      matRange.getColumn(0).format.font.bold = true;
      matRange.getColumn(0).format.font.color = "#52525B";

      // Section "Inventaire des onglets"
      let row = 4 + matRows.length + 2;
      const invHeader = recap.getRangeByIndexes(row, 0, 1, 1);
      invHeader.values = [["INVENTAIRE DES ONGLETS"]];
      invHeader.format.font.bold = true;
      invHeader.format.font.size = 11;
      invHeader.format.font.color = "white";
      invHeader.format.fill.color = "#0F172A";
      row += 1;

      const invRows: string[][] = [];
      invRows.push(["Inputs", det.inputs.join(", ") || "—"]);
      invRows.push(["TB ↔ GL Reconciliation", det.results.includes("Recon_Result") ? "✓ Recon_Result" : "Non lancé"]);
      invRows.push(["Scoping", det.results.includes("Scoping_Result") ? "✓ Scoping_Result" : "Non lancé"]);
      invRows.push(["Reversals", det.results.includes("Reversals_Result") ? "✓ Reversals_Result" : "Non lancé"]);
      invRows.push(["Analytical Review", det.results.includes("Analytical_Result") ? "✓ Analytical_Result" : "Non lancé"]);
      invRows.push(["Sections samplées", det.samples.length > 0 ? `${det.samples.length} (${det.samples.map(s => s.replace("Sample_", "")).join(", ")})` : "Aucune"]);

      const invRange = recap.getRangeByIndexes(row, 0, invRows.length, 2);
      invRange.values = invRows;
      invRange.getColumn(0).format.font.bold = true;
      invRange.getColumn(0).format.font.color = "#52525B";
      row += invRows.length + 2;

      // Section "Statistiques mission"
      const statsHeader = recap.getRangeByIndexes(row, 0, 1, 1);
      statsHeader.values = [["WORKFLOW EXÉCUTÉ"]];
      statsHeader.format.font.bold = true;
      statsHeader.format.font.size = 11;
      statsHeader.format.font.color = "white";
      statsHeader.format.fill.color = "#0F172A";
      row += 1;

      // On essaie de lire les stats des onglets de résultats
      let scopingStats = "—";
      let reversalsStats = "—";
      let analyticalStats = "—";
      let reconStats = "—";

      for (const resultSheet of ["Scoping_Result", "Reversals_Result", "Analytical_Result", "Recon_Result"]) {
        if (sheets.items.find((s: any) => s.name === resultSheet)) {
          try {
            // Lire la ligne de sous-titre (row 2 ou 3 selon convention)
            const sh = sheets.items.find((s: any) => s.name === resultSheet)!;
            const r = sh.getRangeByIndexes(1, 0, 1, 1);
            r.load("values");
            await context.sync();
            const subtitle = String(r.values[0][0] || "");
            if (resultSheet === "Scoping_Result" && subtitle) scopingStats = subtitle;
            if (resultSheet === "Reversals_Result" && subtitle) reversalsStats = subtitle;
            if (resultSheet === "Analytical_Result" && subtitle) analyticalStats = subtitle;
            if (resultSheet === "Recon_Result" && subtitle) reconStats = subtitle;
          } catch (e) {
            // Ignore errors lors de la lecture
          }
        }
      }

      const statsRows: string[][] = [
        ["TB ↔ GL Recon", reconStats],
        ["Scoping", scopingStats],
        ["Reversals", reversalsStats],
        ["Analytical Review", analyticalStats],
      ];

      const statsRange = recap.getRangeByIndexes(row, 0, statsRows.length, 2);
      statsRange.values = statsRows;
      statsRange.getColumn(0).format.font.bold = true;
      statsRange.getColumn(0).format.font.color = "#52525B";
      row += statsRows.length + 2;

      // Pied de page
      const footer = recap.getRangeByIndexes(row, 0, 1, 1);
      footer.values = [["Généré par Audit Panel — pour usage professionnel uniquement."]];
      footer.format.font.italic = true;
      footer.format.font.color = "#A1A1AA";
      footer.format.font.size = 10;

      // Largeur colonnes
      recap.getRange("A:A").format.columnWidth = 200;
      recap.getRange("B:B").format.columnWidth = 400;

      // === 3. Créer ZZ_Memo ===
      const memo = sheets.add("ZZ_Memo");

      const memoTitle = memo.getRange("A1");
      memoTitle.values = [["Memo méthodologique"]];
      memoTitle.format.font.bold = true;
      memoTitle.format.font.size = 16;

      const memoLines = [
        [""],
        ["MATÉRIALITÉ"],
        ["Materiality globale = seuil au-delà duquel une erreur est jugée significative pour la décision des utilisateurs des comptes."],
        ["Performance Materiality (PM) = seuil utilisé pour le testing, plus bas que la materiality globale pour laisser une marge."],
        ["Clearly Trivial Threshold (CTT) = seuil en-dessous duquel les erreurs ne sont pas accumulées."],
        [""],
        ["TB ↔ GL RECONCILIATION"],
        ["Vérification que la balance comptable (TB) correspond exactement aux écritures du grand livre (GL) compte par compte."],
        ["Tolérance configurable. Statuts : OK (réconcilié), WARNING (écart sous tolérance), KO (écart > tolérance), MISSING_IN_GL/TB (compte manquant d'un côté)."],
        [""],
        ["SCOPING"],
        ["Identification des financial lines significatives à auditer, selon le seuil PM."],
        ["Granularité statutory (Belgian GAAP, ~25 lignes) ou ABCOTD (regroupement audit)."],
        ["Closing balance pour le bilan, mouvements nets pour le P&L."],
        [""],
        ["REVERSALS"],
        ["Détection des écritures qui s'annulent (factures + avoirs, OD croisées)."],
        ["Règle dure : même compte, même montant absolu, sens opposé, dans la fenêtre temporelle."],
        ["Score de classification CERTAIN/PROBABLE basé sur similarité des libellés et proximité temporelle."],
        ["Journaux de paiement (FIN, SAL) exclus par défaut car ce sont des appariements, pas des reversals."],
        [""],
        ["ANALYTICAL REVIEW"],
        ["Comparaison des soldes/mouvements N vs N-1 par financial line ou compte."],
        ["Règle A : variation absolue dépassant un seuil en €."],
        ["Règle B : variation relative dépassant un seuil en %."],
        ["Combinaison OR (l'une ou l'autre flag) ou AND (les deux requises)."],
        [""],
        ["SAMPLING"],
        ["Méthodologie MUS (Monetary Unit Sampling) — AICPA Audit Sampling Guide / ISA 530."],
        ["Sample size = ⌈ (Population × Confidence Factor) / PM ⌉"],
        ["Top stratum (items ≥ PM) testés à 100% (matériel par construction)."],
        ["Random sample sur le low stratum via cumulative monetary intervals avec random seed reproductible."],
        ["Méthodes alternatives : Random simple, All > PM, 100% testing."],
        [""],
        ["REPRODUCTIBILITÉ"],
        ["Tous les samples sont reproductibles avec le même seed."],
        ["Toutes les analyses sont déterministes pour les mêmes inputs et paramètres."],
        [""],
        ["RÉFÉRENCES"],
        ["AICPA Audit Sampling Guide"],
        ["ISA 530 — Audit Sampling"],
        ["IRE Belgian audit standards"],
      ];

      const memoRange = memo.getRangeByIndexes(2, 0, memoLines.length, 1);
      memoRange.values = memoLines;

      // Style headers de section dans le memo
      for (let i = 0; i < memoLines.length; i++) {
        const text = memoLines[i][0];
        if (text === text.toUpperCase() && text.length > 0 && !text.includes(".")) {
          const lineRange = memo.getRangeByIndexes(2 + i, 0, 1, 1);
          lineRange.format.font.bold = true;
          lineRange.format.font.color = "#0F172A";
          lineRange.format.font.size = 12;
        }
      }

      memo.getRange("A:A").format.columnWidth = 700;
      memo.getRange("A:A").format.wrapText = true;

      // === 4. Réordonner les onglets ===
      // Ordre désiré : 00_Recap → inputs → résultats → samples → ZZ_Memo
      // Excel reorder via position index : on déplace en commençant par la fin
      recap.position = 0;
      memo.position = sheets.items.length - 1;

      await context.sync();
    });

    // Recompter les onglets après création
    const detAfter = await detectSheets();

    document.getElementById("exp-total")!.textContent = String(
      detAfter.inputs.length + detAfter.results.length + detAfter.samples.length +
      detAfter.recap.length + detAfter.memo.length + detAfter.others.length
    );
    document.getElementById("exp-samples")!.textContent = String(detAfter.samples.length);
    document.getElementById("exp-rev")!.textContent = detAfter.results.includes("Reversals_Result") ? "✓" : "—";
    document.getElementById("exp-ar")!.textContent = detAfter.results.includes("Analytical_Result") ? "✓" : "—";
    summaryDiv.style.display = "block";

    setExportStatus(`✓ Récap généré. Fais Ctrl+S pour sauvegarder.`, "success");
    document.querySelector<HTMLElement>('.step-btn[data-screen="export"]')!.classList.add("completed");
  } catch (err: any) {
    console.error(err);
    setExportStatus(`Erreur : ${err.message}`, "error");
  } finally {
    btn.disabled = false;
  }
}

// ============================================================
// INIT
// ============================================================
Office.onReady((info) => {
  if (info.host === Office.HostType.Excel) {
    document.querySelectorAll<HTMLElement>(".step-btn").forEach(btn => {
      btn.onclick = () => showScreen(btn.dataset.screen!);
    });

    bindSetupScreen();
    bindReversalsScreen();
    bindAnalyticalScreen();
    bindSamplingScreen();
    refreshMatStrip();
    // Charger les mappings depuis le workbook si présents
    loadMappingsFromWorkbook().then(loaded => {
      if (loaded) {
        // Mettre à jour l'UI : pastilles + dropdowns feuilles + matérialité
        // Délai pour s'assurer que le DOM est rendu
        setTimeout(() => {
          refreshAllMappingDots();
          refreshStepAvailability();
        }, 100);
        refreshStepAvailability();
        // Re-rendre le screen Setup pour refléter les valeurs chargées
        const matInput = document.getElementById("mat-input") as HTMLInputElement;
        if (matInput) matInput.value = formatNumberInput(state.materiality);
        const pmInput = document.getElementById("pm-input") as HTMLInputElement;
        if (pmInput) pmInput.value = String(state.pmPct);
        const cttInput = document.getElementById("ctt-input") as HTMLInputElement;
        if (cttInput) cttInput.value = String(state.cttPct);
        refreshMatStrip();
        // Mettre à jour les dropdowns de feuilles
        for (const role of ["gl", "tb", "tb_n1", "chart"] as const) {
          const sel = document.getElementById(`map-${role}`) as HTMLSelectElement;
          if (sel && state.sheets[role]) {
            sel.value = state.sheets[role]!;
          }
        }
        // Détecter quels onglets de résultat existent et marquer les étapes correspondantes comme "completed"
        // Délai pour laisser Excel finir son init avant la lecture
        setTimeout(() => {
          Excel.run(async (context) => {
          const sheets = context.workbook.worksheets;
          sheets.load("items/name");
          await context.sync();

          const sheetNames = sheets.items.map(s => s.name);

          // Setup : completed dès que mappings chargés
          document.querySelector<HTMLElement>('.step-btn[data-screen="setup"]')!.classList.add("completed");

          // TB Recon
          if (sheetNames.includes("Recon_Result")) {
            document.querySelector<HTMLElement>('.step-btn[data-screen="recon"]')!.classList.add("completed");
          }
          // Scoping
          if (sheetNames.includes("Scoping_Result")) {
            document.querySelector<HTMLElement>('.step-btn[data-screen="scoping"]')!.classList.add("completed");
          }
          // Reversals
          if (sheetNames.includes("Reversals_Result")) {
            document.querySelector<HTMLElement>('.step-btn[data-screen="reversals"]')!.classList.add("completed");
          }
          // Analytical
          if (sheetNames.includes("Analytical_Result")) {
            document.querySelector<HTMLElement>('.step-btn[data-screen="analytical"]')!.classList.add("completed");
          }
          // Sampling : si au moins un onglet Sample_* existe
          if (sheetNames.some(n => n.startsWith("Sample_"))) {
            document.querySelector<HTMLElement>('.step-btn[data-screen="sampling"]')!.classList.add("completed");
          }
          // Export : si l'onglet 00_Recap existe
          if (sheetNames.includes("00_Recap")) {
            document.querySelector<HTMLElement>('.step-btn[data-screen="export"]')!.classList.add("completed");
          }
        }).catch(err => console.warn("Détection onglets résultats échouée:", err));
        }, 500);
        // Re-render des accordéons pour mettre à jour les dropdowns avec les valeurs chargées
        document.querySelectorAll<HTMLElement>(".mapping-accordion").forEach(a => {
          const target = (a.querySelector(".mapping-header") as HTMLElement).dataset.target!;
          const body = document.getElementById(`map-body-${target}`);
          if (body && body.style.display !== "none") {
            renderMappingBody(target as any);
          }
        });
      }
    });

    document.getElementById("scope-btn")!.onclick = runScope;
    document.getElementById("recon-btn")!.onclick = runRecon;
    document.getElementById("rev-btn")!.onclick = runReversals;
    document.getElementById("ar-btn")!.onclick = runAnalytical;
    document.getElementById("sample-load-sections-btn")!.onclick = loadScopingSections;
    document.getElementById("sample-batch-btn")!.onclick = runBatchSampling;
    document.getElementById("export-detect-btn")!.onclick = showDetectedSheets;
    document.getElementById("export-generate-btn")!.onclick = generateExport;
    document.getElementById("export-populations-btn")!.onclick = exportTestedPopulations;
  }
});
